import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Paper,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  TablePagination,
  Snackbar,
  Slider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Kitchen as KitchenIcon,
  RestaurantMenu as RestaurantMenuIcon,
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  Payment as PaymentIcon,
  People as PeopleIcon,
  Warehouse as WarehouseIcon,
  TableBar as TableBarIcon,
  Group as GroupIcon,
  RoomService as RoomServiceIcon,
  Dns as DnsIcon,
  Tv as TvIcon,
  MeetingRoom as MeetingRoomIcon
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import QRMenuSelectionModal from './QRMenuSelectionModal';
import useQuickSaleSettingsAgentBridge from '../../hooks/useQuickSaleSettingsAgentBridge';
import {
  flattenTemplateSelection,
  loadWarehouseStockTemplates,
  saveWarehouseStockTemplate,
  type WarehouseStockTemplate,
} from '../../utils/warehouseStockTemplates';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import StarIcon from '@mui/icons-material/Star';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CropIcon from '@mui/icons-material/Crop';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import PhoneIcon from '@mui/icons-material/Phone';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'; // Plan 2: Kiosk Cihazlari tabi
import NoteAddIcon from '@mui/icons-material/NoteAdd'; // Ürün Açıklamaları tabı
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LockClockIcon from '@mui/icons-material/LockClock';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import { ImageUploadService } from '../../../../accounting/inventory/services/imageUploadService';
import { accountingApi, mainApi } from '../../../../../config/apiClient';
import Cropper from 'react-easy-crop';
import ListItem from '@mui/material/ListItem';
import AddIcon from '@mui/icons-material/Add';
import InventoryCategoryModal from '../../../../accounting/inventory/InventoryCategoryModal';
import QuickSaleProductSelectionModal from './QuickSaleProductSelectionModal';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import PrintIcon from '@mui/icons-material/Print';
import CategoryIcon from '@mui/icons-material/Category';
import ScaleIcon from '@mui/icons-material/Scale';
import scaleApi, { ScaleDto } from '../../../../../services/scaleService';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import ReceiptSettingsModal from '../../settings/ReceiptSettingsModal';

// Yeni import satırı
import PaymentSettingsTab from '../../settings/PaymentSettingsTab';
import type { CreatedBankAccount, CreatedCashbox } from '../../../../common/QuickCreatePaymentTarget';
import PrintingSettingsTab from '../../settings/PrintingSettingsTab';
import PersonnelSettingsTab from '../../settings/PersonnelSettingsTab';
import WarehouseStockSettingsTab from '../../settings/WarehouseStockSettingsTab';
import salesService from '../../../../../services/salesService';
import { DEFAULT_PAVO_CARD_MEDIATOR } from '../../../../../services/pavoMediatorConfig';
import accountingService from '../../../../../services/accountingService';
import { getPersonnel as getPersonnelSql, bulkSetScreenPermissions } from '../../../../../services/migration/personnelDataService';
import { useTranslation } from 'react-i18next';
// ✅ YENİ: Restoran tabları
import RestaurantTableGroupsTab from '../../../restaurant/components/RestaurantTableGroupsTab';
import RestaurantTablesTab from '../../../restaurant/components/RestaurantTablesTab';
import RestaurantWaitersTab from '../../../restaurant/components/RestaurantWaitersTab';
// ✅ YENİ: Kasiyer Atama (Hem Normal Hızlı Satış hem de Restoran için)
import CashierSettingsTab from '../../settings/CashierSettingsTab';
// ✅ YENİ: Müşteri Ekranı (Customer Display) - İkinci ekran için reklam ve sepet sistemi
import ClubEntrySettingsTab from '../../settings/ClubEntrySettingsTab';
// 📝 YENİ: Ürün notu modalındaki hazır açıklamaları yönetme sekmesi
import ItemNotesSettingsTab from '../../settings/ItemNotesSettingsTab';
import PavoDevicesTab from './settings/PavoDevicesTab';
import { TerminalDeviceMode } from '../../../../../services/auth/terminalSession';
import KioskAssignmentTab from './settings/KioskAssignmentTab'; // 🖥️ Plan 2 — Kiosk Cihazlari tab
import CustomerDisplaySettingsTab, { 
  CustomerDisplaySettings, 
  DEFAULT_CUSTOMER_DISPLAY_SETTINGS,
  loadCustomerDisplaySettings,
  saveCustomerDisplaySettingsToApi
} from '../../settings/CustomerDisplaySettingsTab';

export interface QuickSaleScreenSettingsModalProps {
  open: boolean;
  onClose: () => void;
  screenId: string | null;
  companyId: string | null;
  branchId?: string | null;
  screenName?: string;
  onSaveSuccess?: () => void;
  isRestaurantMode?: boolean;
  restaurantScreenId?: number;
  isPackageMode?: boolean;
  packageScreenId?: number;
}

export interface IntegrationVisibilitySettings {
  showPhoneOrders: boolean;
  showYemekSepetiOrders: boolean;
  showTrendyolOrders: boolean;
  showGetirOrders: boolean;
  showMigrosOrders: boolean;
  showKioskOrders: boolean;
  showQrMenuOrders: boolean;
  showEcommerceOrders: boolean;
  autoAcceptEnabled: boolean;
  autoAcceptDelaySeconds: number;
  autoPrintKitchen: boolean;
  autoPrintReceiptOnAccept: boolean;
}

export type PaymentOptionType = 'cash' | 'pos' | 'openAccount' | 'voucher' | 'receipt' | 'bonus' | 'accrual' | 'other' | 'online';

export interface PaymentOption {
  id: string;
  type: PaymentOptionType;
  label: string;
  enabled: boolean;
  cashboxId?: string | null;
  cashboxName?: string | null; // ✅ YENİ: Kasa adı (gün sonu raporu için)
  bankAccountId?: string | null;
  bankAccountName?: string | null; // ✅ YENİ: Banka adı (gün sonu raporu için)
  defaultAccountId?: string | null;
  timestamp?: number;
  subAccountId?: string | null;
  relatedAccount?: string;
  accountId?: string | null; // ✅ YENİ: Diğer ödeme için cari hesap ID
  accountName?: string | null; // ✅ YENİ: Diğer ödeme için cari hesap adı
  // ✅ YENİ: Online ödeme için ek alanlar (3 farklı ödeme yöntemi)
  onlineCashboxId?: string | null; // Nakit kasa ID
  onlineCashboxName?: string | null; // Nakit kasa adı
  onlineBankAccountId?: string | null; // POS banka hesap ID
  onlineBankAccountName?: string | null; // POS banka hesap adı
  onlineSubAccountId?: string | null; // POS alt hesap ID
  // ✅ YENİ: Online buton için (şimdilik nakit kasasına kayıt edilecek)
  onlineDirectCashboxId?: string | null; // Online buton için kasa ID
  onlineDirectCashboxName?: string | null; // Online buton için kasa adı
}

interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

interface InventoryItem {
  id: string;
  name: string;
  categoryId?: string | null;
  warehouseId?: string | null;
  salePrice?: number;
  stockCode?: string;
  barcode?: string;
  imageUrl?: string; // string veya undefined olabilir, null değil
}

interface StockVisibilitySettings {
  warehouses?: string[];
  categories?: { [warehouseId: string]: string[] };
  defaultWarehouseId?: string | null;
  products?: { [warehouseId: string]: { [categoryId: string]: string[] } };
  // ✅ YENİ: Şube paket fiyatını kullan (Depo/Stok tabındaki switch)
  usePackagePrices?: boolean;
}

interface DefinedPrinter {
  id: string;
  name: string;
}

interface ScreenSettings {
  assignedPersonnel?: { [personnelId: string]: boolean };
  paymentOptions?: { [id: string]: Omit<PaymentOption, 'id'> };
  stockVisibility?: StockVisibilitySettings;
  definedPrinters?: DefinedPrinter[];
  categoryPrinterAssignments?: { [categoryId: string]: string | null };
  categoryPrinterAssignments2?: { [categoryId: string]: string | null };
  printingMode?: 'single' | 'category';
  defaultReceiptPrinter?: DefinedPrinter | null;
  personnelPermissions?: { [personnelId: string]: PersonnelPermissions };
  preventNegativeStock?: boolean;
  showProductImages?: boolean; // Yeni eklenen ayar - ürün resimleri gösterilsin mi
  receiptDisplaySettings?: ReceiptSettings;
  prepaidSystemEnabled?: boolean; // Ön ödemeli sistemi etkinleştirme durumu
  useFiscalDeviceForCash?: boolean; // YENİ: Nakit ödemelerde ÖKC kullanılsın mı?
  warnIfFiscalNotConnected?: boolean; // YENİ: Yazarkasa bağlı değilse uyar (varsayılan açık)
  usePavoPayForOtherPayments?: boolean; // Diğer ödemelerde PavoPay kullan
  useExternalPrinterForPavo?: boolean; // PavoPay harici yazıcı
  allowAccountCreationOnSale?: boolean; // YENİ: Satış sırasında cari oluşturmaya izin ver
  showCashChangeModal?: boolean; // YENİ: Nakit ödemede para üstü modalı göster
  useScale?: boolean; // YENİ: Bu kasa tartı kullanıyor mu
  selectedScaleId?: number | null; // YENİ: Seçili tartı (Scales.Id)
  scaleReadMode?: string; // YENİ: live | single
  readWeightBarcode?: boolean; // YENİ: Tartının bastığı ağırlık barkodunu çöz
}

interface Personnel {
  id: string;
  name: string;
  printingMode?: 'single' | 'category';
  defaultReceiptPrinter?: DefinedPrinter | null;
  personnelPermissions?: { [personnelId: string]: PersonnelPermissions };
  preventNegativeStock?: boolean;
  timestamp?: number;
  showProductImages?: boolean; // Yeni eklenen ayar
  receiptDisplaySettings?: ReceiptSettings;
  prepaidSystemEnabled?: boolean; // Ön ödemeli sistem durumunu ekle
  useFiscalDeviceForCash?: boolean; // YENİ
  allowAccountCreationOnSale?: boolean; // YENİ
}
interface Cashbox {
  id: string;
  name: string;
  showProductImages?: boolean; // Yeni eklenen ayar
  receiptDisplaySettings?: ReceiptSettings;
  prepaidSystemEnabled?: boolean; // Ön ödemeli sistem durumunu ekle
  useFiscalDeviceForCash?: boolean; // YENİ
  allowAccountCreationOnSale?: boolean; // YENİ
}
interface Account {
  id: string;
  companyName: string;
}
interface Warehouse {
  id: string;
  name: string;
  isDefault?: boolean;
}
interface ProductCategory {
  id: string;
  name: string;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  isPosAccount?: boolean;
  subAccounts?: { [key: string]: SubAccountForModal };
}

interface SubAccountForModal {
  id: string;
  accountName: string;
  accountNumber: string;
  isPosAccount?: boolean;
}

/**
 * Kategori listesini tek yerden eşle. Muhasebe API'si camelCase döner ama kodun
 * bazı yerleri PascalCase DTO tipiyle yazıldığı için iki yazım da karşılanıyor.
 * Tek fonksiyona alınmasının sebebi: ilk yükleme ile kategori modalı kapanışındaki
 * eşlemeler ayrı yazılmıştı ve kapanış yalnızca PascalCase okuduğu için liste
 * boşalıyor, Depo Stok sekmesindeki kategori/ürünler ekrandan kayboluyordu.
 */
const mapInventoryCategories = (raw: any[]): InventoryCategory[] =>
  raw
    .map((c: any) => ({
      id: c.Id?.toString() || c.id?.toString() || '',
      name: c.Name || c.name || 'İsimsiz Kategori',
      parentId: c.ParentId?.toString() || c.parentId?.toString() || null,
      level: c.Level || c.level || 1,
      createdAt: c.CreatedAt || c.createdAt || '',
      branchesId: null,
    }))
    .filter(c => c.id)
    .sort((a, b) => a.name.localeCompare(b.name));

interface WarehouseSelectionModalProps {
  open: boolean;
  onClose: () => void;
  allWarehouses: Warehouse[];
  selectedIds: string[];
  onSave: (newSelectedIds: string[]) => void;
}

export interface PersonnelPermissions {
  changePrice: boolean;
  applyDiscount: boolean;
  cancel: boolean;
  viewReports: boolean;
  warehouseOperations: boolean;
  viewAccountBalance?: boolean;
  listAccounts?: boolean;
  canQuickAddItem?: boolean; // Yeni Yetki: Hızlı Stok Kaydı
  canAddAccount?: boolean;  // Yeni Yetki: Cari Kaydı
  canPosRefund?: boolean;   // ✅ YENİ: POS İade yetkisi
  canClearCart?: boolean;   // ✅ YENİ: Sepeti Temizle yetkisi
  canViewEndOfDay?: boolean; // ✅ YENİ: Gün Sonu yazdırma yetkisi
}

interface ReceiptSettings {
  companyName: string;
  companyAddress: string;
  taxNumber: string;
  showReceiptHeader: boolean;
  showVat: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean; // Yeni eklenen: Fiş numarası göster
  receiptNumberResetValue: number; // Yeni eklenen: Fiş numarası sıfırlama değeri
  resetAtEndOfDay: boolean; // Gün sonunda sıfırlama varsayılan değeri
  printCopyCount: number; // Birden fazla kopya yazdırma varsayılan değeri
  productGrouping: string;
  standardFooterNote: string;
  thankYouMessage: string;
  receiptWidth: number;
  characterLength: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  headerType?: 'text' | 'logo';
  logoUrl?: string;
  logoMaxWidthPx?: number;  // Frontend'de tanımlı - local service sadece uygular
  logoMaxHeightPx?: number;
}

const WarehouseSelectionModal: React.FC<WarehouseSelectionModalProps> = ({
  open,
  onClose,
  allWarehouses,
  selectedIds,
  onSave,
}) => {
  const { t } = useTranslation('salesSystem');
  const [currentlySelectedIds, setCurrentlySelectedIds] = useState<string[]>(selectedIds);

  useEffect(() => {
    setCurrentlySelectedIds(selectedIds);
  }, [selectedIds, open]);

  const handleToggle = (warehouseId: string) => () => {
    const currentIndex = currentlySelectedIds.indexOf(warehouseId);
    const newChecked = [...currentlySelectedIds];

    if (currentIndex === -1) {
      newChecked.push(warehouseId);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setCurrentlySelectedIds(newChecked);
  };

  const handleSaveSelection = () => {
    onSave(currentlySelectedIds);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{t('quickSaleSettingsModal.warehouseSelectionModal.title')}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {allWarehouses.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: 'center' }} variant="body2">
            {t('quickSaleSettingsModal.warehouseSelectionModal.empty')}
          </Typography>
        ) : (
          <List dense sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: '60vh', overflow: 'auto' }}>
            {allWarehouses.map((warehouse) => {
              const labelId = `warehouse-checkbox-list-label-${warehouse.id}`;
              const isSelected = currentlySelectedIds.includes(warehouse.id);

              return (
                <ListItem
                  key={warehouse.id}
                  disablePadding
                  secondaryAction={
                    warehouse.isDefault ? (
                      <Typography variant="caption" color="text.secondary" sx={{mr: 1}}>
                        {t('quickSaleSettingsModal.warehouseSelectionModal.default')}
                      </Typography>
                    ) : null
                  }
                >
                  <ListItemButton role={undefined} onClick={handleToggle(warehouse.id)} dense>
                    <ListItemIcon sx={{minWidth: 'auto', mr: 1.5}}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={warehouse.name} sx={{m: 0}} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('quickSaleSettingsModal.footer.cancel')}</Button>
        <Button onClick={handleSaveSelection} variant="contained">
          {t('quickSaleSettingsModal.warehouseSelectionModal.addSelected')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const QUICKSALE_SETTINGS_KEY_PREFIX = 'quickSaleSettings_';

const getSettingsKey = (companyId: string | null, screenId: string | null): string | null => {
  if (!companyId || !screenId) return null;
  return `${QUICKSALE_SETTINGS_KEY_PREFIX}${companyId}_${screenId}`;
};

// Firebase undefined değerlerini temizleyen yardımcı fonksiyon
const cleanObject = (obj: any): any => {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => cleanObject(item));

  const cleanedObj: any = {};
  Object.entries(obj).forEach(([key, value]) => {
    // Undefined değerleri null'a çevir, diğerlerini recursive olarak temizle
    cleanedObj[key] = value === undefined ? null : cleanObject(value);
  });
  return cleanedObj;
};

// Ödeme seçeneklerini özel olarak temizleyen fonksiyon
const cleanPaymentOptions = (options: { [id: string]: any }): { [id: string]: any } => {
  const cleanedOptions: { [id: string]: any } = {};
  
  Object.entries(options).forEach(([id, option]) => {
    const cleanedOption: any = {};
    
    // Her alanı kontrol et ve undefined olanları null'a çevir
    Object.entries(option).forEach(([field, value]) => {
      cleanedOption[field] = value === undefined ? null : value;
    });
    
    // Alt hesap ID'si yoksa açıkça null olarak belirle
    if (option.type === 'pos' && !cleanedOption.subAccountId) {
      cleanedOption.subAccountId = null;
    }
    
    cleanedOptions[id] = cleanedOption;
  });
  
  return cleanedOptions;
};

// Yardımcı fonksiyonlar (kırpma için)
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

// Resmi belirtilen koordinatlara göre kırpma
async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context is not available');
  }

  // Kare kırpma için boyut ayarla
  canvas.width = 200;
  canvas.height = 200;

  // Kırpılan bölgeyi beyaz arka plan üzerine çiz
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Kırpılan kısmı canvas'a çiz
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    200,
    200
  );

  // Canvas'ı blob'a dönüştür
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.95 // Kalite
    );
  });
}

// Resim yükleme modal bileşeni
interface ProductImageModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  loading: boolean;
}

const ProductImageModal: React.FC<ProductImageModalProps> = ({ open, onClose, onUpload, loading }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Resim kırpma için state'ler
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [showCropper, setShowCropper] = useState(false);

  // İzin verilen dosya formatları
  const allowedFormats = ['jpg', 'jpeg', 'webp'];

  // Dosya seçildiğinde
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      
      // Dosya uzantısını al ve kontrol et
      const fileNameParts = file.name.split('.');
      const extension = fileNameParts[fileNameParts.length - 1].toLowerCase();
      
      if (allowedFormats.includes(extension)) {
        setSelectedFile(file);
        setFileError(null);
        
        // Dosyayı URL'ye çevir ve kırpma için hazırla
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            setImageSrc(reader.result);
            setShowCropper(true);
          }
        };
        reader.readAsDataURL(file);
      } else {
        setSelectedFile(null);
        setImageSrc(null);
        setShowCropper(false);
        setFileError(`Sadece ${allowedFormats.join(', ')} formatındaki resimler kabul edilmektedir.`);
        // Input'u temizle
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  // Kırpma tamamlandığında
  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Resmi yükle butonuna tıklandığında
  const handleUploadClick = async () => {
    if (imageSrc && croppedAreaPixels) {
      try {
        // Resmi kırp
        const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
        
        // Blob'u File'a dönüştür
        const originalFileName = selectedFile?.name || 'cropped-image.jpg';
        const fileExtension = originalFileName.split('.').pop() || 'jpg';
        const croppedFile = new File(
          [croppedImageBlob], 
          `cropped-${Date.now()}.${fileExtension}`, 
          { type: `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}` }
        );
        
        // Resmi yükle
        await onUpload(croppedFile);
        
        // State'leri temizle
        setSelectedFile(null);
        setImageSrc(null);
        setShowCropper(false);
        setFileError(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setFileError('Resim kırpma işlemi sırasında bir hata oluştu.');
        console.error('Kırpma hatası:', error);
      }
    }
  };

  // Modal kapanırken durumu sıfırla
  const handleClose = () => {
    setFileError(null);
    setSelectedFile(null);
    setImageSrc(null);
    setShowCropper(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: showCropper ? 500 : 'auto'
        }
      }}
    >
      <DialogTitle>
        Ürün Resmi Yükle
        <IconButton
          aria-label="kapat"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {showCropper && imageSrc ? (
          <Box sx={{ my: 2, height: 350, position: 'relative' }}>
            <Box sx={{ position: 'relative', width: '100%', height: 300, backgroundColor: '#f0f0f0' }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropSize={{ width: 200, height: 200 }}
                showGrid={true}
              />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <ZoomOutIcon sx={{ color: 'text.secondary' }} />
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e, zoom) => setZoom(zoom as number)}
                sx={{ mx: 2, flexGrow: 1 }}
              />
              <ZoomInIcon sx={{ color: 'text.secondary' }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Resmi istediğiniz şekilde konumlandırıp, zoom ile boyutunu ayarlayabilirsiniz. Kare alan görüntülenecektir.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ my: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg, image/jpg, image/webp"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="product-image-input"
            />
            <label htmlFor="product-image-input">
              <Button
                variant="contained"
                component="span"
                fullWidth
                startIcon={<CropIcon />}
              >
                Resim Seç
              </Button>
            </label>
            {fileError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {fileError}
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Not: Sadece JPG, JPEG ve WEBP formatındaki resimler kabul edilmektedir. 
              Resimler 200x200 boyutunda kare olarak kırpılacaktır.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          İptal
        </Button>
        <Button 
          onClick={handleUploadClick} 
          color="primary" 
          variant="contained"
          disabled={!imageSrc || !!fileError || loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Yükle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const QuickSaleScreenSettingsModal: React.FC<QuickSaleScreenSettingsModalProps> = ({
  open,
  onClose,
  screenId,
  companyId,
  branchId,
  screenName,
  onSaveSuccess,
  isRestaurantMode = false,
  restaurantScreenId,
  isPackageMode = false,
  packageScreenId,
}) => {
  const { t } = useTranslation('salesSystem');
  // ✅ Mobil kontrolü
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── Satış terminali kodu bağlamı ("Login İşlemleri" sekmesi) ──────────────
  // PavoDevicesTab paket modunu 'quicksale' sayıyor; satış terminalinde paket
  // ayrı bir mod olduğu için o eşleme buraya kopyalanmıyor.
  const terminalDeviceMode: TerminalDeviceMode = isRestaurantMode
    ? 'sales_restaurant'
    : isPackageMode
      ? 'sales_package'
      : 'sales_quicksale';

  // Restoran modunda kod RestaurantScreens.Id'ye bağlanır — bağlı
  // QuickSaleScreenId'ye değil; /restaurant-screen/:setupId bu Id'yi bekliyor.
  const parsedQuickSaleScreenId = screenId ? parseInt(screenId, 10) : NaN;
  const terminalLinkedScreenId = isRestaurantMode
    ? (restaurantScreenId ?? null)
    : isPackageMode
      ? (packageScreenId ?? null)
      : (Number.isFinite(parsedQuickSaleScreenId) ? parsedQuickSaleScreenId : null);
  
  // ✅ Mobil tab drawer state'i
  const [mobileTabDrawerOpen, setMobileTabDrawerOpen] = useState(false);
  
  
  const [settings, setSettings] = useState<ScreenSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clubEntryChanged, setClubEntryChanged] = useState(false);

  const [allPersonnel, setAllPersonnel] = useState<Personnel[]>([]);
  const [allCashboxes, setAllCashboxes] = useState<Cashbox[]>([]);
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [allBankAccounts, setAllBankAccounts] = useState<BankAccount[]>([]);
  const [allWarehouses, setAllWarehouses] = useState<Warehouse[]>([]);
  const [allCategories, setAllCategories] = useState<InventoryCategory[]>([]);
  // ⚠️ allInventoryItems artık gerekli değil - QuickSaleProductSelectionModal kendi veriyi çekecek

  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState<string[]>([]);
  const [initialSelectedPersonnelIds, setInitialSelectedPersonnelIds] = useState<string[]>([]);
  const [selectedPersonnelForPermissions, setSelectedPersonnelForPermissions] = useState<string | null>(null);
  const [personnelPermissionsMap, setPersonnelPermissionsMap] = useState<{[personnelId: string]: PersonnelPermissions}>({});
  const [initialPersonnelPermissions, setInitialPersonnelPermissions] = useState<{[personnelId: string]: PersonnelPermissions}>({});
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
  const [initialPaymentOptions, setInitialPaymentOptions] = useState<PaymentOption[]>([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>([]);
  const [initialSelectedWarehouseIds, setInitialSelectedWarehouseIds] = useState<string[]>([]);
  const [selectedCategoriesPerWarehouse, setSelectedCategoriesPerWarehouse] = useState<{ [warehouseId: string]: string[] }>({});
  const [initialSelectedCategoriesPerWarehouse, setInitialSelectedCategoriesPerWarehouse] = useState<{ [warehouseId: string]: string[] }>({});

  const [activeTab, setActiveTab] = useState(0);
  
  // ✅ YENİ: Entegrasyon görünürlük ayarları (Paket modu için)
  const [integrationSettings, setIntegrationSettings] = useState<IntegrationVisibilitySettings>({
    showPhoneOrders: true,
    showYemekSepetiOrders: true,
    showTrendyolOrders: true,
    showGetirOrders: true,
    showMigrosOrders: true,
    showKioskOrders: true,
    showQrMenuOrders: true,
    showEcommerceOrders: true,
    autoAcceptEnabled: false,
    autoAcceptDelaySeconds: 15,
    autoPrintKitchen: false,
    autoPrintReceiptOnAccept: false,
  });
  const [initialIntegrationSettings, setInitialIntegrationSettings] = useState<IntegrationVisibilitySettings>({
    showPhoneOrders: true,
    showYemekSepetiOrders: true,
    showTrendyolOrders: true,
    showGetirOrders: true,
    showMigrosOrders: true,
    showKioskOrders: true,
    showQrMenuOrders: true,
    showEcommerceOrders: true,
    autoAcceptEnabled: false,
    autoAcceptDelaySeconds: 15,
    autoPrintKitchen: false,
    autoPrintReceiptOnAccept: false,
  });
  
  // Modal açıldığında/kapandığında activeTab'i sıfırla
  useEffect(() => {
    if (open) {
      console.log('🔵 Modal açıldı - activeTab sıfırlanıyor');
      console.log('🔵 isRestaurantMode:', isRestaurantMode);
      console.log('🔵 restaurantScreenId:', restaurantScreenId);
      console.log('🔵 isPackageMode:', isPackageMode);
      console.log('🔵 packageScreenId:', packageScreenId);
      setActiveTab(0);
    }
  }, [open, isRestaurantMode, isPackageMode]);
  
  // YENİ: Ek Ayarlar tab için state'ler
  // (Burada ilerleyen adımlarda yeni ayarlar eklenecek)
  const [isWarehouseSelectionModalOpen, setIsWarehouseSelectionModalOpen] = useState(false);
  const [activeWarehouseSubTab, setActiveWarehouseSubTab] = useState(0);
  const [activeCategorySubTabPerWarehouse, setActiveCategorySubTabPerWarehouse] = useState<{ [warehouseId: string]: number }>({});
  const [isInventoryCategoryModalOpen, setIsInventoryCategoryModalOpen] = useState(false);
  // Ürün kategorileri modalı "Depo Stok" sekmesinden açıldığında hedef depo:
  // modal içindeki "Ekle" butonu kategoriyi bu deponun listesine yazar.
  const [currentWarehouseIdForCategoryModal, setCurrentWarehouseIdForCategoryModal] = useState<string | null>(null);
  const [isProductSelectionModalOpen, setIsProductSelectionModalOpen] = useState(false);
  const [currentWarehouseIdForProductModal, setCurrentWarehouseIdForProductModal] = useState<string | null>(null);
  const [currentCategoryIdForProductModal, setCurrentCategoryIdForProductModal] = useState<string | null>(null);

  const [selectedProductsPerCategory, setSelectedProductsPerCategory] = useState<{ [warehouseId: string]: { [categoryId: string]: string[] } }>({});
  const [initialSelectedProductsPerCategory, setInitialSelectedProductsPerCategory] = useState<{ [warehouseId: string]: { [categoryId: string]: string[] } }>({});
  // ⚠️ YENİ: Seçilen ürünlerin tam bilgilerini tutacak state
  const [selectedProductsDetails, setSelectedProductsDetails] = useState<{ [productId: string]: any }>({});

  const [selectedProductsPage, setSelectedProductsPage] = useState<{ [key: string]: number }>({});
  const [selectedProductsRowsPerPage, setSelectedProductsRowsPerPage] = useState<{ [key: string]: number }>({});

  const [definedPrinters, setDefinedPrinters] = useState<DefinedPrinter[]>([]);
  const [newPrinterName, setNewPrinterName] = useState("");
  const [categoryPrinterAssignments, setCategoryPrinterAssignments] = useState<{ [categoryId: string]: string | null }>({});
  // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel) — fiş her iki yazıcıya da gider
  const [categoryPrinterAssignments2, setCategoryPrinterAssignments2] = useState<{ [categoryId: string]: string | null }>({});
  const [initialDefinedPrinters, setInitialDefinedPrinters] = useState<DefinedPrinter[]>([]);
  const [initialCategoryPrinterAssignments, setInitialCategoryPrinterAssignments] = useState<{ [categoryId: string]: string | null }>({});
  const [initialCategoryPrinterAssignments2, setInitialCategoryPrinterAssignments2] = useState<{ [categoryId: string]: string | null }>({});

  const [localPrinters, setLocalPrinters] = useState<DefinedPrinter[]>([]);
  const [loadingLocalPrinters, setLoadingLocalPrinters] = useState(false);
  const [localPrintersError, setLocalPrintersError] = useState<string | null>(null);

  const [printingMode, setPrintingMode] = useState<'single' | 'category'>('category');
  const [defaultReceiptPrinter, setDefaultReceiptPrinter] = useState<DefinedPrinter | null>(null);
  const [initialPrintingMode, setInitialPrintingMode] = useState<'single' | 'category'>('category');
  const [initialDefaultReceiptPrinter, setInitialDefaultReceiptPrinter] = useState<DefinedPrinter | null>(null);
  
  // ✅ YENİ: Adisyon yazıcısı (kategori bazlı modda restoran için)
  const [adisyonPrinter, setAdisyonPrinter] = useState<DefinedPrinter | null>(null);
  const [initialAdisyonPrinter, setInitialAdisyonPrinter] = useState<DefinedPrinter | null>(null);
  
  // ✅ YENİ: Kategorisiz ürün yazıcısı (kategori bazlı modda — kategorisi olmayan ürünler için)
  const [uncategorizedPrinter, setUncategorizedPrinter] = useState<DefinedPrinter | null>(null);
  const [initialUncategorizedPrinter, setInitialUncategorizedPrinter] = useState<DefinedPrinter | null>(null);
  
  // ✅ YENİ: Yazarkasa + Fiş Yazıcısı birlikte kullanım (varsayılan KAPALI)
  const [useBothFiscalAndReceiptPrinter, setUseBothFiscalAndReceiptPrinter] = useState<boolean>(false);
  const [initialUseBothFiscalAndReceiptPrinter, setInitialUseBothFiscalAndReceiptPrinter] = useState<boolean>(false);

  const [preventNegativeStock, setPreventNegativeStock] = useState<boolean>(false);
  const [initialPreventNegativeStock, setInitialPreventNegativeStock] = useState<boolean>(false);
  const [showProductImages, setShowProductImages] = useState<boolean>(true); // Varsayılan olarak açık
  // ✅ YENİ: Şube paket fiyatını kullan (StockVisibilityJson.usePackagePrices)
  const [usePackagePrices, setUsePackagePrices] = useState<boolean>(false);
  const [initialUsePackagePrices, setInitialUsePackagePrices] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [receiptSettings, setReceiptSettings] = React.useState<ReceiptSettings>({
    companyName: 'AQUASOFT',
    companyAddress: '',
    taxNumber: '',
    showReceiptHeader: true,
    showVat: true,
    showDiscountInfo: true,
    showCashierName: true,
    showReceiptNumber: false, // Yeni eklenen varsayılan değer
    receiptNumberResetValue: 999999, // Yeni eklenen varsayılan değer
    resetAtEndOfDay: false, // Gün sonunda sıfırlama varsayılan değeri
    printCopyCount: 1, // Birden fazla kopya yazdırma varsayılan değeri
    productGrouping: '',
    standardFooterNote: 'Bizi tercih ettiğiniz için teşekkür ederiz.',
    thankYouMessage: 'İyi günler!',
    receiptWidth: 80,
    characterLength: 48,
    marginTop: 5,
    marginBottom: 5,
    marginLeft: 5,
    marginRight: 5,
  });
  const [initialReceiptDisplaySettings, setInitialReceiptDisplaySettings] = useState<ReceiptSettings | null>(null);

  const [prepaidSystemEnabled, setPrepaidSystemEnabled] = useState<boolean>(false);
  const [initialPrepaidSystemEnabled, setInitialPrepaidSystemEnabled] = useState<boolean>(false);

  // YENİ STATE'LER ÖKC KULLANIMI İÇİN
  // ✅ Varsayılan AÇIK - Nakit ödemelerde ÖKC zorunlu kullanılacak
  const [useFiscalDeviceForCash, setUseFiscalDeviceForCash] = useState<boolean>(true);
  const [initialUseFiscalDeviceForCash, setInitialUseFiscalDeviceForCash] = useState<boolean>(true);

  // YENİ STATE: Yazarkasa markası seçimi
  const [selectedFiscalDevice, setSelectedFiscalDevice] = useState<string>('hugin_t300');
  const [initialSelectedFiscalDevice, setInitialSelectedFiscalDevice] = useState<string>('hugin_t300');

  // ✅ YENİ STATE: Yazarkasa bağlı değilse uyarı göster (varsayılan AÇIK)
  const [warnIfFiscalNotConnected, setWarnIfFiscalNotConnected] = useState<boolean>(true);
  const [initialWarnIfFiscalNotConnected, setInitialWarnIfFiscalNotConnected] = useState<boolean>(true);

  // Diğer ödemelerde PavoPay kullan (varsayılan KAPALI)
  const [usePavoPayForOtherPayments, setUsePavoPayForOtherPayments] = useState<boolean>(false);
  const [initialUsePavoPayForOtherPayments, setInitialUsePavoPayForOtherPayments] = useState<boolean>(false);

  // PavoPay harici yazıcı kullan (varsayılan KAPALI = dahili 58mm)
  const [useExternalPrinterForPavo, setUseExternalPrinterForPavo] = useState<boolean>(false);
  const [initialUseExternalPrinterForPavo, setInitialUseExternalPrinterForPavo] = useState<boolean>(false);

  // Kartlı ödemede Pavo'ya gönderilecek ödeme aracısı (varsayılan BKM/TechPOS).
  // Albaraka BKM'de olmadığı için Albaraka POS'lu şirketler AlbarakaPOS (38) seçer.
  const [pavoPaymentMediatorId, setPavoPaymentMediatorId] = useState<number>(DEFAULT_PAVO_CARD_MEDIATOR);
  const [initialPavoPaymentMediatorId, setInitialPavoPaymentMediatorId] = useState<number>(DEFAULT_PAVO_CARD_MEDIATOR);

  // ✅ YENİ: Rest-P2P modu (NAKIT/POS/Karma ödemeleri SignalR üzerinden Pavo cihazına gönder)
  const [useRestP2PForPayments, setUseRestP2PForPayments] = useState<boolean>(false);
  const [initialUseRestP2PForPayments, setInitialUseRestP2PForPayments] = useState<boolean>(false);
  const [pavoRestP2PDeviceScreenCode, setPavoRestP2PDeviceScreenCode] = useState<string | null>(null);
  const [initialPavoRestP2PDeviceScreenCode, setInitialPavoRestP2PDeviceScreenCode] = useState<string | null>(null);
  const [pavoRestP2PDeviceName, setPavoRestP2PDeviceName] = useState<string | null>(null);
  const [initialPavoRestP2PDeviceName, setInitialPavoRestP2PDeviceName] = useState<string | null>(null);

  // YENİ STATE: Satış sırasında cari oluşturma izni
  const [allowAccountCreationOnSale, setAllowAccountCreationOnSale] = useState<boolean>(false);
  const [initialAllowAccountCreationOnSale, setInitialAllowAccountCreationOnSale] = useState<boolean>(false);

  // ✅ YENİ STATE: Cari tahsilat izni (ekran seviyesi ana kapı)
  const [allowAccountCollection, setAllowAccountCollection] = useState<boolean>(false);
  const [initialAllowAccountCollection, setInitialAllowAccountCollection] = useState<boolean>(false);

  // YENİ STATE: Para Üstü Modalı
  const [showCashChangeModal, setShowCashChangeModal] = useState<boolean>(false);
  const [initialShowCashChangeModal, setInitialShowCashChangeModal] = useState<boolean>(false);

  // YENİ STATE: Mutfak Ekranı Kullanımı
  const [useKitchenScreen, setUseKitchenScreen] = useState<boolean>(false);
  const [showScreenLayoutButton, setShowScreenLayoutButton] = useState<boolean>(true);
  const [initialShowScreenLayoutButton, setInitialShowScreenLayoutButton] = useState<boolean>(true);
  // YENİ STATE: Kasiyer Sistemi Kullanımı
  const [useCashierSystem, setUseCashierSystem] = useState<boolean>(false);
  const [initialUseCashierSystem, setInitialUseCashierSystem] = useState<boolean>(false);
  const [initialUseKitchenScreen, setInitialUseKitchenScreen] = useState<boolean>(false);
  
  // YENİ STATE: Oto Kilitleme
  const [autoLockEnabled, setAutoLockEnabled] = useState<boolean>(false);
  const [initialAutoLockEnabled, setInitialAutoLockEnabled] = useState<boolean>(false);
  const [autoLockTimeoutSeconds, setAutoLockTimeoutSeconds] = useState<number>(30);
  const [initialAutoLockTimeoutSeconds, setInitialAutoLockTimeoutSeconds] = useState<number>(30);

  // YENİ STATE: Hazır Silme Nedenleri
  const [predefinedDeleteReasons, setPredefinedDeleteReasons] = useState<string[]>([]);
  const [initialPredefinedDeleteReasons, setInitialPredefinedDeleteReasons] = useState<string[]>([]);
  const [newDeleteReason, setNewDeleteReason] = useState<string>('');

  // YENİ STATE: Hazır Ürün Açıklamaları (ürün notu modalındaki tek dokunuşluk notlar)
  const [predefinedItemNotes, setPredefinedItemNotes] = useState<string[]>([]);
  const [initialPredefinedItemNotes, setInitialPredefinedItemNotes] = useState<string[]>([]);

  // YENİ STATE: Yerel Servis URL'si
  const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';
  const [localServiceUrl, setLocalServiceUrl] = useState<string>(DEFAULT_LOCAL_SERVICE_URL);
  const [initialLocalServiceUrl, setInitialLocalServiceUrl] = useState<string>(DEFAULT_LOCAL_SERVICE_URL);

  // Tarti: cihaz tanimi Tartilar ekraninda (SQL), burada yalnizca hangisinin
  // kullanilacagi seciliyor. Agirlik barkodu okumak tartiya baglanmayi
  // gerektirmedigi icin ayri anahtar.
  const [useScale, setUseScale] = useState<boolean>(false);
  const [initialUseScale, setInitialUseScale] = useState<boolean>(false);
  const [selectedScaleId, setSelectedScaleId] = useState<number | null>(null);
  const [initialSelectedScaleId, setInitialSelectedScaleId] = useState<number | null>(null);
  const [scaleReadMode, setScaleReadMode] = useState<string>('live');
  const [initialScaleReadMode, setInitialScaleReadMode] = useState<string>('live');
  const [readWeightBarcode, setReadWeightBarcode] = useState<boolean>(false);
  const [initialReadWeightBarcode, setInitialReadWeightBarcode] = useState<boolean>(false);
  const [availableScales, setAvailableScales] = useState<ScaleDto[]>([]);
  
  // ✅ YENİ: Müşteri Ekranı (Customer Display) State'leri
  const [customerDisplaySettings, setCustomerDisplaySettings] = useState<CustomerDisplaySettings>({ ...DEFAULT_CUSTOMER_DISPLAY_SETTINGS });
  const [initialCustomerDisplaySettings, setInitialCustomerDisplaySettings] = useState<CustomerDisplaySettings>({ ...DEFAULT_CUSTOMER_DISPLAY_SETTINGS });
  
  const [selectedQRMenuId, setSelectedQRMenuId] = useState<string | null>(null);
  const [initialSelectedQRMenuId, setInitialSelectedQRMenuId] = useState<string | null>(null);
  const [selectedQRMenuName, setSelectedQRMenuName] = useState<string>('');
  const [isQRMenuSelectionModalOpen, setIsQRMenuSelectionModalOpen] = useState<boolean>(false);

  const uniqueCategoryIds = useMemo(() => {
    const allSelectedCatIds = Object.values(selectedCategoriesPerWarehouse).flat();
    return Array.from(new Set(allSelectedCatIds));
  }, [selectedCategoriesPerWarehouse]);

  const categoriesToListForPrinting = useMemo(() => {
    return uniqueCategoryIds
      .map(catId => allCategories.find(c => c.id === catId))
      .filter((c): c is InventoryCategory => !!c)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [uniqueCategoryIds, allCategories]);

  useEffect(() => {
    // Yazdırma tab'ı: Normal mod Tab 4, Restoran mod Tab 7
    if (open && ((isRestaurantMode && activeTab === 7) || (!isRestaurantMode && activeTab === 4))) {
      const fetchLocalPrinters = async () => {
        setLoadingLocalPrinters(true);
        setLocalPrintersError(null);
        setLocalPrinters([]); 
        try {
          const response = await fetch('http://localhost:3003/api/yazicilariGetir');
          if (!response.ok) {
            let errorMessage = `Yerel yazıcı servisine ulaşılamadı (Kod: ${response.status})`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
            } catch (e) {
                errorMessage = `${errorMessage}: ${response.statusText}`;
            }
            throw new Error(errorMessage);
          }
          const printersFromService: Array<{ id: string; name: string }> = await response.json();
          setLocalPrinters(printersFromService.map(p => ({ id: p.id, name: p.name })));
        } catch (err: any) {
          console.error("Yerel yazıcılar alınırken hata:", err);
          setLocalPrintersError(err.message || 'Yerel yazıcılar alınamadı. Lütfen yerel servisin çalıştığından emin olun.');
        } finally {
          setLoadingLocalPrinters(false);
        }
      };
      fetchLocalPrinters();
    }
  }, [open, activeTab, isRestaurantMode]);

  // Seçilebilecek tartılar. Pasif cihazlar listede görünmemeli; tanım
  // Cihaz Yönetimi > Tartılar ekranında yapılıyor.
  useEffect(() => {
    if (!open) return;
    scaleApi
      .getAll({ activeOnly: true })
      .then(setAvailableScales)
      .catch(() => setAvailableScales([]));
  }, [open]);

  useEffect(() => {
    if (open && screenId && companyId) {
      setLoading(true);
      setError(null);
      setInitialReceiptDisplaySettings(null);
      setInitialPaymentOptions([]); 
      setInitialSelectedWarehouseIds([]);
      setInitialSelectedCategoriesPerWarehouse({});
      setInitialSelectedProductsPerCategory({});
      setInitialDefinedPrinters([]);
      setInitialCategoryPrinterAssignments({});
      setInitialPrintingMode('category');
      setInitialDefaultReceiptPrinter(null);
      setInitialPersonnelPermissions({});
      setInitialPreventNegativeStock(false);
      setInitialPrepaidSystemEnabled(false);
      setInitialUseFiscalDeviceForCash(false);
      setInitialAllowAccountCreationOnSale(false);
      setAllowAccountCollection(false);
      setInitialAllowAccountCollection(false);
      setActiveTab(0);
      setActiveWarehouseSubTab(0);
      setActiveCategorySubTabPerWarehouse({});
      setIsWarehouseSelectionModalOpen(false);
      setCurrentWarehouseIdForCategoryModal(null);
      setIsInventoryCategoryModalOpen(false);
      setIsProductSelectionModalOpen(false);
      setCurrentWarehouseIdForProductModal(null);
      setCurrentCategoryIdForProductModal(null);
      setSelectedProductsPage({});
      setSelectedProductsRowsPerPage({});
      setNewPrinterName("");
      setAllPersonnel([]);
      setAllCashboxes([]);
      setAllAccounts([]);
      setAllBankAccounts([]);
      setAllWarehouses([]);
      setAllCategories([]);
      // ⚠️ allInventoryItems artık kullanılmıyor

      const fetchData = async () => {
        try {
          // ========== SQL'DEN EKRAN AYARLARINI ÇEK ==========
          const screenIdAsNumber = parseInt(screenId, 10);
          if (isNaN(screenIdAsNumber)) {
            throw new Error(`Geçersiz ekran ID'si: ${screenId}`);
          }

          const screenData = await salesService.getQuickSaleScreenById(screenIdAsNumber);
          
          // Payment Options JSON'dan parse et
          let loadedPaymentOptions: PaymentOption[] = [];
          if (screenData.PaymentOptionsJson) {
            try {
              loadedPaymentOptions = JSON.parse(screenData.PaymentOptionsJson);
              setPaymentOptions(loadedPaymentOptions);
              setInitialPaymentOptions(JSON.parse(JSON.stringify(loadedPaymentOptions)));
            } catch (e) {
              console.error("PaymentOptionsJson parse hatası:", e);
            }
          }

          // Assigned Personnel JSON'dan parse et
          let loadedAssignedPersonnel: { [personnelId: string]: boolean } = {};
          if (screenData.AssignedPersonnelJson) {
            try {
              loadedAssignedPersonnel = JSON.parse(screenData.AssignedPersonnelJson);
              const personnelIds = Object.keys(loadedAssignedPersonnel).filter(id => loadedAssignedPersonnel[id]);
              setSelectedPersonnelIds(personnelIds);
              setInitialSelectedPersonnelIds([...personnelIds]); // Initial değeri kaydet
            } catch (e) {
              console.error("AssignedPersonnelJson parse hatası:", e);
            }
          }

          // Personnel Permissions JSON'dan parse et
          let loadedPersonnelPermissions: { [personnelId: string]: PersonnelPermissions } = {};
          if (screenData.PersonnelPermissionsJson) {
            try {
              loadedPersonnelPermissions = JSON.parse(screenData.PersonnelPermissionsJson);
              setPersonnelPermissionsMap(loadedPersonnelPermissions);
              setInitialPersonnelPermissions(JSON.parse(JSON.stringify(loadedPersonnelPermissions)));
            } catch (e) {
              console.error("PersonnelPermissionsJson parse hatası:", e);
            }
          }

          // Stock Visibility JSON'dan parse et
          if (screenData.StockVisibilityJson) {
            try {
              const stockVisibility: StockVisibilitySettings = JSON.parse(screenData.StockVisibilityJson);
              setSelectedWarehouseIds(stockVisibility.warehouses || []);
              setInitialSelectedWarehouseIds(stockVisibility.warehouses || []);
              setSelectedCategoriesPerWarehouse(stockVisibility.categories || {});
              setInitialSelectedCategoriesPerWarehouse(stockVisibility.categories || {});
              setSelectedProductsPerCategory(stockVisibility.products || {});
              setInitialSelectedProductsPerCategory(stockVisibility.products || {});
              // ✅ YENİ: Şube paket fiyatı kullan bayrağı
              const usePkg = stockVisibility.usePackagePrices === true;
              setUsePackagePrices(usePkg);
              setInitialUsePackagePrices(usePkg);
            } catch (e) {
              console.error("StockVisibilityJson parse hatası:", e);
            }
          }

          // Printing Settings JSON'dan parse et
          if (screenData.PrintingSettingsJson) {
            try {
              const printingSettings = JSON.parse(screenData.PrintingSettingsJson);
              setPrintingMode(printingSettings.printingMode || 'category');
              setInitialPrintingMode(printingSettings.printingMode || 'category');
              setDefaultReceiptPrinter(printingSettings.defaultReceiptPrinter || null);
              setInitialDefaultReceiptPrinter(printingSettings.defaultReceiptPrinter || null);
              setDefinedPrinters(printingSettings.definedPrinters || []);
              setInitialDefinedPrinters(printingSettings.definedPrinters || []);
              setCategoryPrinterAssignments(printingSettings.categoryPrinterAssignments || {});
              setInitialCategoryPrinterAssignments(printingSettings.categoryPrinterAssignments || {});
              setCategoryPrinterAssignments2(printingSettings.categoryPrinterAssignments2 || {});
              setInitialCategoryPrinterAssignments2(printingSettings.categoryPrinterAssignments2 || {});
              if (printingSettings.receiptDisplaySettings) {
                setReceiptSettings(printingSettings.receiptDisplaySettings);
                setInitialReceiptDisplaySettings(printingSettings.receiptDisplaySettings);
              }
              // ✅ YENİ: Adisyon yazıcısı
              setAdisyonPrinter(printingSettings.adisyonPrinter || null);
              setInitialAdisyonPrinter(printingSettings.adisyonPrinter || null);
              // ✅ YENİ: Kategorisiz ürün yazıcısı
              setUncategorizedPrinter(printingSettings.uncategorizedPrinter || null);
              setInitialUncategorizedPrinter(printingSettings.uncategorizedPrinter || null);
              // ✅ YENİ: Yazarkasa + Fiş Yazıcısı birlikte kullanım
              setUseBothFiscalAndReceiptPrinter(printingSettings.useBothFiscalAndReceiptPrinter || false);
              setInitialUseBothFiscalAndReceiptPrinter(printingSettings.useBothFiscalAndReceiptPrinter || false);
            } catch (e) {
              console.error("PrintingSettingsJson parse hatası:", e);
            }
          }

          // Boolean ayarlar
          setPrepaidSystemEnabled(screenData.PrepaidSystemEnabled);
          setInitialPrepaidSystemEnabled(screenData.PrepaidSystemEnabled);
          setShowProductImages(screenData.ShowProductImages);
          setUseFiscalDeviceForCash(screenData.UseFiscalDeviceForCash);
          setInitialUseFiscalDeviceForCash(screenData.UseFiscalDeviceForCash);
          console.log("🔍 [FISCAL DEVICE] SQL'den gelen değer:", screenData.SelectedFiscalDevice); // DEBUG
          setSelectedFiscalDevice(screenData.SelectedFiscalDevice || 'hugin_t300'); // YENİ: Yazarkasa markası
          setInitialSelectedFiscalDevice(screenData.SelectedFiscalDevice || 'hugin_t300'); // YENİ
          // ✅ YENİ: Yazarkasa bağlantı uyarısı (varsayılan true)
          const warnIfNotConnected = screenData.WarnIfFiscalNotConnected !== undefined ? screenData.WarnIfFiscalNotConnected : true;
          setWarnIfFiscalNotConnected(warnIfNotConnected);
          setInitialWarnIfFiscalNotConnected(warnIfNotConnected);
          const pavoForOther = screenData.UsePavoPayForOtherPayments === true;
          setUsePavoPayForOtherPayments(pavoForOther);
          setInitialUsePavoPayForOtherPayments(pavoForOther);
          const externalPrinter = screenData.UseExternalPrinterForPavo === true;
          setUseExternalPrinterForPavo(externalPrinter);
          setInitialUseExternalPrinterForPavo(externalPrinter);
          // Kolon eklenmeden önce kaydedilmiş ekranlar 0/undefined döndürebilir;
          // geçersiz değerde BKM/TechPOS varsayılanına düşülür.
          const cardMediator = Number((screenData as any).PavoPaymentMediatorId);
          const resolvedMediator = Number.isFinite(cardMediator) && cardMediator > 0
            ? cardMediator
            : DEFAULT_PAVO_CARD_MEDIATOR;
          setPavoPaymentMediatorId(resolvedMediator);
          setInitialPavoPaymentMediatorId(resolvedMediator);
          // ✅ YENİ: Rest-P2P modu
          const restP2P = (screenData as any).UseRestP2PForPayments === true;
          setUseRestP2PForPayments(restP2P);
          setInitialUseRestP2PForPayments(restP2P);
          const restP2PCode = (screenData as any).PavoRestP2PDeviceScreenCode || null;
          setPavoRestP2PDeviceScreenCode(restP2PCode);
          setInitialPavoRestP2PDeviceScreenCode(restP2PCode);
          setAllowAccountCreationOnSale(screenData.AllowAccountCreationOnSale);
          setInitialAllowAccountCreationOnSale(screenData.AllowAccountCreationOnSale);
          setAllowAccountCollection((screenData as any).AllowAccountCollection === true);
          setInitialAllowAccountCollection((screenData as any).AllowAccountCollection === true);
          setShowCashChangeModal(screenData.ShowCashChangeModal || false);
          setInitialShowCashChangeModal(screenData.ShowCashChangeModal || false);
          const loadedUseScale = (screenData as any).UseScale === true;
          const loadedScaleId = (screenData as any).SelectedScaleId ?? null;
          const loadedReadMode = (screenData as any).ScaleReadMode || 'live';
          const loadedWeightBarcode = (screenData as any).ReadWeightBarcode === true;
          setUseScale(loadedUseScale);
          setInitialUseScale(loadedUseScale);
          setSelectedScaleId(loadedScaleId);
          setInitialSelectedScaleId(loadedScaleId);
          setScaleReadMode(loadedReadMode);
          setInitialScaleReadMode(loadedReadMode);
          setReadWeightBarcode(loadedWeightBarcode);
          setInitialReadWeightBarcode(loadedWeightBarcode);
          setUseKitchenScreen(screenData.UseKitchenScreen || false);
          setInitialUseKitchenScreen(screenData.UseKitchenScreen || false);
          setShowScreenLayoutButton(screenData.ShowScreenLayoutButton !== false);
          setInitialShowScreenLayoutButton(screenData.ShowScreenLayoutButton !== false);
          setSelectedQRMenuId(screenData.SelectedQRMenuId || null);
          setInitialSelectedQRMenuId(screenData.SelectedQRMenuId || null);
          setUseCashierSystem(screenData.UseCashierSystem || false);
          setInitialUseCashierSystem(screenData.UseCashierSystem || false);
          
          setAutoLockEnabled(screenData.AutoLockEnabled || false);
          setInitialAutoLockEnabled(screenData.AutoLockEnabled || false);
          setAutoLockTimeoutSeconds(screenData.AutoLockTimeoutSeconds || 30);
          setInitialAutoLockTimeoutSeconds(screenData.AutoLockTimeoutSeconds || 30);

          try {
            const reasons = screenData.PredefinedDeleteReasonsJson
              ? JSON.parse(screenData.PredefinedDeleteReasonsJson)
              : [];
            setPredefinedDeleteReasons(reasons);
            setInitialPredefinedDeleteReasons(reasons);
          } catch { setPredefinedDeleteReasons([]); setInitialPredefinedDeleteReasons([]); }

          try {
            const itemNotes = screenData.PredefinedItemNotesJson
              ? JSON.parse(screenData.PredefinedItemNotesJson)
              : [];
            setPredefinedItemNotes(itemNotes);
            setInitialPredefinedItemNotes(itemNotes);
          } catch { setPredefinedItemNotes([]); setInitialPredefinedItemNotes([]); }
          
          // YENİ: Yerel Servis URL'si
          setLocalServiceUrl(screenData.LocalServiceUrl || DEFAULT_LOCAL_SERVICE_URL);
          setInitialLocalServiceUrl(screenData.LocalServiceUrl || DEFAULT_LOCAL_SERVICE_URL);
          console.log("🔌 [LOCAL SERVICE] SQL'den gelen değer:", screenData.LocalServiceUrl || DEFAULT_LOCAL_SERVICE_URL);

          // ✅ YENİ: Müşteri Ekranı (Customer Display) ayarlarını yükle
          try {
            const cdSettings = await loadCustomerDisplaySettings(screenIdAsNumber);
            setCustomerDisplaySettings(cdSettings);
            setInitialCustomerDisplaySettings({ ...cdSettings });
            console.log("📺 [CUSTOMER DISPLAY] Ayarlar yüklendi:", cdSettings);
          } catch (cdErr) {
            console.error("❌ [CUSTOMER DISPLAY] Ayarlar yüklenirken hata:", cdErr);
          }
          
          // ✅ YENİ: Paket modu ise entegrasyon ayarlarını yükle
          if (isPackageMode && packageScreenId) {
            try {
              const packageScreenData = await salesService.getPackageScreenById(packageScreenId);
              const loadedIntegrationSettings: IntegrationVisibilitySettings = {
                showPhoneOrders: packageScreenData.ShowPhoneOrders ?? true,
                showYemekSepetiOrders: packageScreenData.ShowYemekSepetiOrders ?? true,
                showTrendyolOrders: packageScreenData.ShowTrendyolOrders ?? true,
                showGetirOrders: packageScreenData.ShowGetirOrders ?? true,
                showMigrosOrders: packageScreenData.ShowMigrosOrders ?? true,
                showKioskOrders: packageScreenData.ShowKioskOrders ?? true,
                showQrMenuOrders: packageScreenData.ShowQrMenuOrders ?? true,
                showEcommerceOrders: packageScreenData.ShowEcommerceOrders ?? true,
                autoAcceptEnabled: packageScreenData.AutoAcceptEnabled ?? false,
                autoAcceptDelaySeconds: packageScreenData.AutoAcceptDelaySeconds ?? 15,
                autoPrintKitchen: packageScreenData.AutoPrintKitchen ?? false,
                autoPrintReceiptOnAccept: packageScreenData.AutoPrintReceiptOnAccept ?? false,
              };
              setIntegrationSettings(loadedIntegrationSettings);
              setInitialIntegrationSettings({ ...loadedIntegrationSettings });
              console.log("🔌 [ENTEGRASYONLAR] Ayarlar yüklendi:", loadedIntegrationSettings);
            } catch (intErr) {
              console.error("❌ [ENTEGRASYONLAR] Ayarlar yüklenirken hata:", intErr);
            }
          }

          console.log("✅ Ekran ayarları SQL'den yüklendi");

          // ========== PERSONEL LİSTESİNİ ÇEK (SQL-once + HybridFallback) ==========
          // Faz 3: personnelDataService.getPersonnel (id=uid). RTDB doğrudan okuma kaldırıldı.
          try {
            const sqlPersonnel = await getPersonnelSql(companyId);
            const personnelList: Personnel[] = sqlPersonnel.map((p) => ({
              id: p.id, // uid
              name: p.name || 'İsimsiz Personel',
            }));
            setAllPersonnel(personnelList);
            console.log(`✅ ${personnelList.length} personel SQL'den yüklendi`);
          } catch (err) {
            console.error("❌ Personel listesi yüklenemedi:", err);
            setAllPersonnel([]);
          }

          // ========== SQL'DEN DEPO VE KATEGORİ LİSTESİNİ ÇEK ==========
          // (Ürünler lazy loading ile çekilecek - modal açıldığında tüm ürünler yüklenmesin)
          
          try {
            // DEPO LİSTESİ
            const warehousesData = await accountingService.getWarehouses();
            if (Array.isArray(warehousesData)) {
              const warehouseList: Warehouse[] = warehousesData
                .map((w: any) => ({
                  id: w.Id?.toString() || w.id?.toString() || '',
                  name: w.Name || w.name || 'İsimsiz Depo',
                  isDefault: w.IsDefault || w.isDefault || false,
                }))
                .filter(w => w.id) // Boş id'leri filtrele
                .sort((a, b) => {
                  if (a.isDefault && !b.isDefault) return -1;
                  if (!a.isDefault && b.isDefault) return 1;
                  return a.name.localeCompare(b.name);
                });
              setAllWarehouses(warehouseList);
              console.log(`✅ ${warehouseList.length} depo SQL'den yüklendi`);
            } else {
              console.warn('Depo verisi array formatında değil');
              setAllWarehouses([]);
            }
          } catch (err) {
            console.error("❌ Depo listesi yüklenemedi:", err);
            setAllWarehouses([]);
          }

          // KATEGORİ LİSTESİ
          try {
            const categoriesData = await accountingService.getCategories();
            if (Array.isArray(categoriesData)) {
              const categoryList = mapInventoryCategories(categoriesData);
              setAllCategories(categoryList);
              console.log(`✅ ${categoryList.length} kategori SQL'den yüklendi`);
            } else {
              console.warn('Kategori verisi array formatında değil');
              setAllCategories([]);
            }
          } catch (err) {
            console.error("❌ Kategori listesi yüklenemedi:", err);
            setAllCategories([]);
          }

          // ⚠️ ÜRÜN LİSTESİ ARTIK BURADA YÜKLENMİYOR!
          // Ürünler, QuickSaleProductSelectionModal açıldığında sayfalama ile yüklenecek
          // Bu sayede modal açılırken binlerce ürün yüklenmeyecek
          console.log("✅ Ürün listesi lazy loading ile yüklenecek (modal açıldığında)");

        } catch (err) {
          console.error("❌ Ayarlar yüklenirken hata:", err);
          setError("Ayarlar yüklenirken bir hata oluştu.");
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [open, screenId, companyId]);

  // ✅ FIX: Seçili ürün ID'lerinden ürün detaylarını SQL'den çek (ID'lere göre)
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!companyId || !open) return;

      // Tüm seçili ürün ID'lerini topla
      const allProductIds: string[] = [];
      Object.values(selectedProductsPerCategory).forEach(warehouseProducts => {
        Object.values(warehouseProducts).forEach(productIds => {
          allProductIds.push(...productIds);
        });
      });

      // Unique ID'leri al
      const uniqueProductIds = Array.from(new Set(allProductIds));
      
      if (uniqueProductIds.length === 0) {
        setSelectedProductsDetails({});
        return;
      }

      // Zaten yüklü olan ürünleri atla
      const missingProductIds = uniqueProductIds.filter(id => !selectedProductsDetails[id]);
      
      if (missingProductIds.length === 0) {
        console.log('✅ [PRODUCT DETAILS] Tüm ürün detayları zaten yüklü');
        return;
      }

      console.log(`📦 [PRODUCT DETAILS] ${missingProductIds.length} ürün detayı ID'lere göre yükleniyor...`);

      try {
        // ✅ FIX: ID'lere göre ürünleri çek (genel sayfalama yerine)
        const items = await salesService.getProductsByIds(missingProductIds, {
          includeWarehouseStocks: false,
        });
        
        // Yeni detayları oluştur
        const newDetails: { [productId: string]: any } = {};
        items.forEach((item: any) => {
          const itemId = item.id?.toString() || item.Id?.toString();
          if (itemId) {
            newDetails[itemId] = {
              id: itemId,
              name: item.name || item.Name || '',
              barcode: item.barcode || item.Barcode || '',
              stockCode: item.stockCode || item.StockCode || '',
              quantity: item.totalQuantity || item.TotalQuantity || 0,
              unit: item.unit || item.Unit || '',
              salePrice: item.salePrice || item.SalePrice || 0,
              imageUrl: item.imageUrl || item.ImageUrl || null,
              categoryId: item.categoryId || item.CategoryId || null,
            };
          }
        });

        // Mevcut detaylarla birleştir (mevcut olanları korur)
        setSelectedProductsDetails(prev => ({
          ...prev,
          ...newDetails
        }));
        
        // ⚠️ Bulunamayan ürünleri logla
        const foundIds = Object.keys(newDetails);
        const notFoundIds = missingProductIds.filter(id => !foundIds.includes(id));
        if (notFoundIds.length > 0) {
          console.warn(`⚠️ [PRODUCT DETAILS] ${notFoundIds.length} ürün bulunamadı:`, notFoundIds);
        }
        
        console.log(`✅ [PRODUCT DETAILS] ${Object.keys(newDetails).length}/${missingProductIds.length} ürün detayı eklendi`);
      } catch (error) {
        console.error('❌ [PRODUCT DETAILS] Ürün detayları yüklenirken hata:', error);
      }
    };

    fetchProductDetails();
  }, [selectedProductsPerCategory, companyId, open]);

  // Ödeme Yöntemleri tab'ı açıldığında Kasa ve Banka Hesapları çek
  // Normal mod Tab 0, Restoran mod Tab 2
  useEffect(() => {
    if (open && ((isRestaurantMode && activeTab === 2) || (!isRestaurantMode && activeTab === 0)) && companyId) {
      // KASA LİSTESİ (Nakit ödeme için gerekli)
      if (allCashboxes.length === 0) {
        accountingService.getCashboxes()
          .then(cashboxData => {
            if (Array.isArray(cashboxData)) {
              const cashboxList: Cashbox[] = cashboxData.map((c: any) => ({
                id: (c?.Id || c?.id)?.toString() || '',
                name: c?.Name || c?.name || 'İsimsiz Kasa'
              })).filter(c => c.id); // Boş id'leri filtrele
              setAllCashboxes(cashboxList);
              console.log(`✅ ${cashboxList.length} kasa SQL'den yüklendi`);
            } else {
              console.warn('Kasa verisi array formatında değil');
              setAllCashboxes([]);
            }
          })
          .catch(err => {
            console.error("❌ Kasa listesi yüklenemedi:", err);
            setAllCashboxes([]);
          });
      }

      // BANKA HESAPLARI (POS ödeme için gerekli)
      if (allBankAccounts.length === 0) {
        accountingService.getBankAccounts()
          .then(bankData => {
            console.log('🔍 API\'den gelen RAW banka verisi:', bankData);
            
            if (Array.isArray(bankData)) {
              const bankList: BankAccount[] = bankData.map((data: any) => {
                console.log('🔍 İşlenen banka hesabı:', {
                  id: data.id || data.Id,
                  bankName: data.bankName || data.BankName,
                  subAccounts: data.subAccounts || data.SubAccounts
                });
                
                // Alt hesapları dönüştür (Bank.tsx mantığı ile aynı)
                const subAccountsObj: { [key: string]: SubAccountForModal } = {};
                const subAccountsArray = data.subAccounts || data.SubAccounts;
                
                if (subAccountsArray && Array.isArray(subAccountsArray)) {
                  subAccountsArray.forEach((subAccount: any) => {
                    const subId = (subAccount.id || subAccount.Id)?.toString();
                    if (subId) {
                      subAccountsObj[subId] = {
                        id: subId,
                        accountName: subAccount.accountName || subAccount.AccountName || 'İsimsiz Alt Hesap',
                        accountNumber: subAccount.accountNumber || subAccount.AccountNumber || '-',
                        isPosAccount: subAccount.isPosAccount || subAccount.IsPosAccount || false
                      };
                    }
                  });
                }
                
                const bankId = (data.id || data.Id)?.toString();
                const bankAccount = {
                  id: bankId || '',
                  bankName: data.bankName || data.BankName || 'İsimsiz Banka',
                  accountNumber: data.accountNumber || data.AccountNumber || '-',
                  isPosAccount: data.isPosAccount || data.IsPosAccount || false,
                  subAccounts: Object.keys(subAccountsObj).length > 0 ? subAccountsObj : undefined
                };
                
                console.log('✅ Oluşturulan banka hesabı:', bankAccount);
                return bankAccount;
              }).filter(b => b.id); // Boş id'leri filtrele
              
              setAllBankAccounts(bankList);
              console.log(`✅ ${bankList.length} banka hesabı SQL'den yüklendi - FINAL:`, bankList);
            } else {
              console.warn('Banka hesap verisi array formatında değil');
              setAllBankAccounts([]);
            }
          })
          .catch(err => {
            console.error("❌ Banka hesap listesi yüklenemedi:", err);
            setAllBankAccounts([]);
          });
      }

      // ✅ CARİ HESAPLARI ("Diğer Ödeme" için gerekli)
      if (allAccounts.length === 0) {
        accountingService.getAccounts()
          .then(accountsData => {
            if (Array.isArray(accountsData)) {
              const accountList: Account[] = accountsData
                .filter((acc: any) => acc.isActive !== false) // Sadece aktif cariler
                .map((acc: any) => ({
                  id: (acc.id || acc.Id)?.toString() || '',
                  companyName: acc.companyName || acc.CompanyName || 'İsimsiz Cari'
                }))
                .filter(acc => acc.id); // Boş id'leri filtrele
              setAllAccounts(accountList);
              console.log(`✅ ${accountList.length} cari hesap SQL'den yüklendi`);
            } else {
              console.warn('Cari hesap verisi array formatında değil');
              setAllAccounts([]);
            }
          })
          .catch(err => {
            console.error("❌ Cari hesap listesi yüklenemedi:", err);
            setAllAccounts([]);
          });
      }
    }
  }, [open, activeTab, companyId, isRestaurantMode]);

  const handleSave = async () => {
    if (!screenId || !companyId) {
      setError("Ekran ID veya Şirket ID bulunamadı.");
      return;
    }
    
    // YENİ VALIDATION: Mutfak ekranı aktifse QR menü seçimi zorunlu
    if (useKitchenScreen && !selectedQRMenuId) {
      setError("Mutfak ekranı entegrasyonu aktif edildiğinde QR menü seçimi zorunludur. Lütfen bir QR menü seçin.");
      setActiveTab(isRestaurantMode ? 7 : 4); // Ek Ayarlar tabına geç (Restoran: Tab 7, Normal: Tab 4)
      return;
    }
    
    setSaving(true);
    setError(null);

    try {
      // screenId'yi number'a çevir
      const screenIdAsNumber = parseInt(screenId, 10);
      if (isNaN(screenIdAsNumber)) {
        throw new Error(`Geçersiz ekran ID'si: ${screenId}`);
      }
      
      // ========== SQL'E KAYDET: TÜM SEKMELER ==========
      
      // TAB 0: Ödeme Yöntemleri
      const paymentOptionsJson = JSON.stringify(paymentOptions);
      
      // TAB 1: Personel Atama ve Yetkileri
      const updatedAssignedPersonnel: { [personnelId: string]: boolean } = {};
      allPersonnel.forEach(p => {
        updatedAssignedPersonnel[p.id] = selectedPersonnelIds.includes(p.id);
      });
      const assignedPersonnelJson = JSON.stringify(updatedAssignedPersonnel);
      
      const updatedPersonnelPermissions: { [personnelId: string]: PersonnelPermissions } = {};
      selectedPersonnelIds.forEach(id => {
        updatedPersonnelPermissions[id] = personnelPermissionsMap[id] || {
          changePrice: false,
          applyDiscount: false,
          cancel: false,
          viewReports: false,
          warehouseOperations: false,
          viewAccountBalance: false,
          listAccounts: false,
          canQuickAddItem: false,
          canAddAccount: false
        };
      });
      const personnelPermissionsJson = JSON.stringify(updatedPersonnelPermissions);
      
      // TAB 2: Depo/Stok Görünürlüğü
      const updatedStockVisibility: StockVisibilitySettings = {
        warehouses: selectedWarehouseIds,
        categories: selectedCategoriesPerWarehouse,
        products: selectedProductsPerCategory,
        defaultWarehouseId: null,
        // ✅ YENİ: Şube paket fiyatı kullan - sadece ekrana şube tanımlıysa anlamlı
        usePackagePrices: !!branchId && usePackagePrices,
      };
      const stockVisibilityJson = JSON.stringify(updatedStockVisibility);
      
      // TAB 3: Yazdırma Ayarları
      const printingSettings = {
        printingMode: printingMode,
        defaultReceiptPrinter: printingMode === 'single' ? defaultReceiptPrinter : null,
        definedPrinters: printingMode === 'category' ? definedPrinters : [],
        categoryPrinterAssignments: printingMode === 'category' ? categoryPrinterAssignments : {},
        // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel)
        categoryPrinterAssignments2: printingMode === 'category' ? categoryPrinterAssignments2 : {},
        receiptDisplaySettings: receiptSettings,
        adisyonPrinter: printingMode === 'category' ? adisyonPrinter : null,
        // ✅ YENİ: Kategorisiz ürün yazıcısı (sadece kategori modunda)
        uncategorizedPrinter: printingMode === 'category' ? uncategorizedPrinter : null,
        // ✅ YENİ: Yazarkasa + Fiş Yazıcısı birlikte kullanım
        useBothFiscalAndReceiptPrinter: useBothFiscalAndReceiptPrinter,
      };
      const printingSettingsJson = JSON.stringify(printingSettings);
      
      // SQL'e kaydet
      try {
        // 🔴 DEBUG: API'ye gönderilecek UseCashierSystem değerini logla
        console.log('═══════════════════════════════════════════════════════════');
        console.log('🔴 [SAVE] API\'ye gönderilecek veriler:');
        console.log('🔴 [SAVE] screenIdAsNumber:', screenIdAsNumber);
        console.log('🔴 [SAVE] useCashierSystem VALUE:', useCashierSystem);
        console.log('🔴 [SAVE] typeof useCashierSystem:', typeof useCashierSystem);
        console.log('═══════════════════════════════════════════════════════════');
        
        const updateDto = {
          Id: screenIdAsNumber,
          PaymentOptionsJson: paymentOptionsJson,
          AssignedPersonnelJson: assignedPersonnelJson,
          PersonnelPermissionsJson: personnelPermissionsJson,
          StockVisibilityJson: stockVisibilityJson,
          PrintingSettingsJson: printingSettingsJson,
          PrepaidSystemEnabled: prepaidSystemEnabled,
          ShowProductImages: showProductImages,
          UseFiscalDeviceForCash: useFiscalDeviceForCash,
          SelectedFiscalDevice: selectedFiscalDevice, // YENİ: Yazarkasa markası
          WarnIfFiscalNotConnected: warnIfFiscalNotConnected, // ✅ YENİ: Yazarkasa bağlantı uyarısı
          UsePavoPayForOtherPayments: usePavoPayForOtherPayments,
          UseExternalPrinterForPavo: useExternalPrinterForPavo,
          PavoPaymentMediatorId: pavoPaymentMediatorId, // Kartlı ödemede Pavo ödeme aracısı
          UseRestP2PForPayments: useRestP2PForPayments, // ✅ YENİ: Rest-P2P modu
          PavoRestP2PDeviceScreenCode: pavoRestP2PDeviceScreenCode, // ✅ YENİ: Rest-P2P cihaz kodu
          AllowAccountCreationOnSale: allowAccountCreationOnSale,
          AllowAccountCollection: allowAccountCollection, // ✅ YENİ: Cari tahsilat izni
          ShowCashChangeModal: showCashChangeModal, // YENİ: Para üstü modalı
          UseScale: useScale, // YENİ: Tartı kullan
          SelectedScaleId: selectedScaleId, // YENİ: Seçili tartı
          ScaleReadMode: scaleReadMode, // YENİ: Okuma şekli
          ReadWeightBarcode: readWeightBarcode, // YENİ: Ağırlık barkodu oku
          UseKitchenScreen: useKitchenScreen,
          ShowScreenLayoutButton: showScreenLayoutButton,
          SelectedQRMenuId: selectedQRMenuId,
          UseCashierSystem: useCashierSystem,
          LocalServiceUrl: localServiceUrl,
          AutoLockEnabled: autoLockEnabled,
          AutoLockTimeoutSeconds: autoLockTimeoutSeconds,
          PredefinedDeleteReasonsJson: JSON.stringify(predefinedDeleteReasons),
          PredefinedItemNotesJson: JSON.stringify(predefinedItemNotes),
        };
        
        console.log('🔴 [SAVE] Tam DTO:', JSON.stringify(updateDto, null, 2));
        
        const result = await salesService.updateQuickSaleScreen(updateDto);
        
        console.log('🟢 [SAVE] API yanıtı:', JSON.stringify(result, null, 2));
        console.log("✅ Tüm ayarlar SQL'e kaydedildi");
      } catch (sqlErr) {
        console.error("❌ SQL'e kayıt hatası:", sqlErr);
        throw new Error(`SQL kayıt hatası: ${sqlErr instanceof Error ? sqlErr.message : String(sqlErr)}`);
      }

      // ========== PERSONEL EKRAN YETKİLERİ: SQL'E KAYDET (SystemUserScreenPermissions) ==========
      // Faz 3: personel-bazlı quickSaleScreens yetkisi SQL'de TEK KAYNAK. RTDB'ye YAZILMAZ.
      // (Detaylı yetki bayraklari PersonnelPermissionsJson olarak QuickSaleScreen SQL kaydinda;
      //  burada yalniz erisim/atama bayragi isEnabled olarak SystemUserScreenPermissions'a yazilir.)
      try {
        const permItems = allPersonnel.map((personnel) => ({
          firebaseUid: personnel.id,
          screenType: 'quickSaleScreens',
          screenId: String(screenId),
          isEnabled: selectedPersonnelIds.includes(personnel.id),
        }));
        if (permItems.length > 0) {
          await bulkSetScreenPermissions(permItems);
          console.log(`✅ ${permItems.length} personel için quickSaleScreens yetkisi SQL'e kaydedildi`);
        }
      } catch (permErr) {
        console.error("❌ Personel ekran yetkisi SQL kayıt hatası:", permErr);
        console.warn("⚠️ SQL ekran ayarları kaydedildi ama personel yetki kaydı başarısız oldu");
      }
      
      // ========== PAKET MODU: ENTEGRASYON AYARLARINI KAYDET ==========
      if (isPackageMode && packageScreenId) {
        try {
          const integrationUpdateDto = {
            Id: packageScreenId,
            ShowPhoneOrders: integrationSettings.showPhoneOrders,
            ShowYemekSepetiOrders: integrationSettings.showYemekSepetiOrders,
            ShowTrendyolOrders: integrationSettings.showTrendyolOrders,
            ShowGetirOrders: integrationSettings.showGetirOrders,
            ShowMigrosOrders: integrationSettings.showMigrosOrders,
            ShowKioskOrders: integrationSettings.showKioskOrders,
            ShowQrMenuOrders: integrationSettings.showQrMenuOrders,
            ShowEcommerceOrders: integrationSettings.showEcommerceOrders,
            AutoAcceptEnabled: integrationSettings.autoAcceptEnabled,
            AutoAcceptDelaySeconds: integrationSettings.autoAcceptDelaySeconds,
            AutoPrintKitchen: integrationSettings.autoPrintKitchen,
            AutoPrintReceiptOnAccept: integrationSettings.autoPrintReceiptOnAccept,
          };
          
          await salesService.updatePackageScreen(integrationUpdateDto);
          console.log("🔌 [ENTEGRASYONLAR] Ayarlar PackageScreen'e kaydedildi:", integrationUpdateDto);
          
          // Initial state'i güncelle
          setInitialIntegrationSettings({ ...integrationSettings });
        } catch (intErr) {
          console.error("❌ [ENTEGRASYONLAR] Kayıt hatası:", intErr);
          // Entegrasyon hatası kritik değil, diğer ayarlar kaydedildi
          console.warn("⚠️ Entegrasyon ayarları kaydedilemedi ama diğer ayarlar tamam");
        }
      }

      // localStorage'a kaydet
      const settingsKey = getSettingsKey(companyId, screenId);
      if (settingsKey) {
        try {
          const settingsForLocalStorage = {
            paymentOptions: paymentOptions,
            assignedPersonnel: updatedAssignedPersonnel,
            personnelPermissions: updatedPersonnelPermissions,
            stockVisibility: updatedStockVisibility,
            printingMode: printingMode,
            defaultReceiptPrinter: printingMode === 'single' ? defaultReceiptPrinter : null,
            definedPrinters: printingMode === 'category' ? definedPrinters : [],
            categoryPrinterAssignments: printingMode === 'category' ? categoryPrinterAssignments : {},
            categoryPrinterAssignments2: printingMode === 'category' ? categoryPrinterAssignments2 : {},
            receiptDisplaySettings: receiptSettings,
            prepaidSystemEnabled: prepaidSystemEnabled,
            showProductImages: showProductImages,
            useFiscalDeviceForCash: useFiscalDeviceForCash,
            selectedFiscalDevice: selectedFiscalDevice, // YENİ: Yazarkasa markası
            warnIfFiscalNotConnected: warnIfFiscalNotConnected, // ✅ YENİ: Yazarkasa bağlantı uyarısı
            usePavoPayForOtherPayments: usePavoPayForOtherPayments,
            useExternalPrinterForPavo: useExternalPrinterForPavo,
            pavoPaymentMediatorId: pavoPaymentMediatorId,
            useRestP2PForPayments: useRestP2PForPayments, // ✅ YENİ
            pavoRestP2PDeviceScreenCode: pavoRestP2PDeviceScreenCode, // ✅ YENİ
            pavoRestP2PDeviceName: pavoRestP2PDeviceName, // ✅ YENİ
            allowAccountCreationOnSale: allowAccountCreationOnSale,
            allowAccountCollection: allowAccountCollection, // ✅ YENİ: Cari tahsilat izni
            showCashChangeModal: showCashChangeModal, // YENİ: Para üstü modalı
            useScale: useScale, // YENİ: Tartı kullan
            selectedScaleId: selectedScaleId, // YENİ: Seçili tartı
            scaleReadMode: scaleReadMode, // YENİ: Okuma şekli
            readWeightBarcode: readWeightBarcode, // YENİ: Ağırlık barkodu oku
            useKitchenScreen: useKitchenScreen,
            showScreenLayoutButton: showScreenLayoutButton,
            selectedQRMenuId: selectedQRMenuId,
            useCashierSystem: useCashierSystem,
            autoLockEnabled: autoLockEnabled,
            autoLockTimeoutSeconds: autoLockTimeoutSeconds,
            predefinedDeleteReasons: predefinedDeleteReasons,
            predefinedItemNotes: predefinedItemNotes,
            localServiceUrl: localServiceUrl,
            preventNegativeStock: preventNegativeStock,
            lastUpdated: new Date().toISOString()
          };
          
          localStorage.setItem(settingsKey, JSON.stringify(settingsForLocalStorage));
          console.log(`✅ Ayarlar localStorage'a kaydedildi: ${settingsKey}`);
        } catch (localStorageError) {
          console.error("❌ localStorage kayıt hatası:", localStorageError);
        }
      }

      // Initial state'leri güncelle
      setPaymentOptions(JSON.parse(JSON.stringify(paymentOptions)));
      setInitialPaymentOptions(JSON.parse(JSON.stringify(paymentOptions)));
      setInitialSelectedPersonnelIds([...selectedPersonnelIds]); // YENİ: Personel atama initial değeri
      setInitialSelectedWarehouseIds([...selectedWarehouseIds]);
      setInitialSelectedCategoriesPerWarehouse(JSON.parse(JSON.stringify(selectedCategoriesPerWarehouse)));
      setInitialSelectedProductsPerCategory(JSON.parse(JSON.stringify(selectedProductsPerCategory)));
      setInitialDefinedPrinters(JSON.parse(JSON.stringify(printingMode === 'category' ? definedPrinters : [])));
      setInitialCategoryPrinterAssignments(JSON.parse(JSON.stringify(printingMode === 'category' ? categoryPrinterAssignments : {})));
      setInitialCategoryPrinterAssignments2(JSON.parse(JSON.stringify(printingMode === 'category' ? categoryPrinterAssignments2 : {})));
      setInitialPrintingMode(printingMode);
      setInitialDefaultReceiptPrinter(printingMode === 'single' ? defaultReceiptPrinter : null);
      setInitialAdisyonPrinter(printingMode === 'category' ? adisyonPrinter : null); // YENİ: Adisyon yazıcısı
      setInitialUncategorizedPrinter(printingMode === 'category' ? uncategorizedPrinter : null); // YENİ: Kategorisiz ürün yazıcısı
      setInitialUseBothFiscalAndReceiptPrinter(useBothFiscalAndReceiptPrinter); // YENİ: Yazarkasa+Fiş
      setInitialPersonnelPermissions(JSON.parse(JSON.stringify(updatedPersonnelPermissions)));
      setInitialPreventNegativeStock(preventNegativeStock);
      setInitialUsePackagePrices(usePackagePrices); // ✅ YENİ: Şube paket fiyatı kullan
      setInitialReceiptDisplaySettings(JSON.parse(JSON.stringify(receiptSettings)));
      setInitialPrepaidSystemEnabled(prepaidSystemEnabled);
      setInitialUseFiscalDeviceForCash(useFiscalDeviceForCash);
      setInitialSelectedFiscalDevice(selectedFiscalDevice); // YENİ: Yazarkasa markası
      setInitialWarnIfFiscalNotConnected(warnIfFiscalNotConnected); // ✅ YENİ: Yazarkasa bağlantı uyarısı
      setInitialUsePavoPayForOtherPayments(usePavoPayForOtherPayments);
      setInitialUseExternalPrinterForPavo(useExternalPrinterForPavo);
      setInitialPavoPaymentMediatorId(pavoPaymentMediatorId);
      setInitialUseRestP2PForPayments(useRestP2PForPayments); // ✅ YENİ
      setInitialPavoRestP2PDeviceScreenCode(pavoRestP2PDeviceScreenCode); // ✅ YENİ
      setInitialPavoRestP2PDeviceName(pavoRestP2PDeviceName); // ✅ YENİ
      setInitialAllowAccountCreationOnSale(allowAccountCreationOnSale);
      setInitialAllowAccountCollection(allowAccountCollection); // ✅ YENİ: Cari tahsilat izni
      setInitialShowCashChangeModal(showCashChangeModal); // YENİ: Para üstü modalı
      setInitialUseScale(useScale); // YENİ: Tartı ayarları
      setInitialSelectedScaleId(selectedScaleId);
      setInitialScaleReadMode(scaleReadMode);
      setInitialReadWeightBarcode(readWeightBarcode);
      setInitialUseKitchenScreen(useKitchenScreen);
      setInitialShowScreenLayoutButton(showScreenLayoutButton);
      setInitialSelectedQRMenuId(selectedQRMenuId);
      setInitialUseCashierSystem(useCashierSystem); // YENİ: Kasiyer sistemi
      setInitialLocalServiceUrl(localServiceUrl); // YENİ: Yerel servis URL'si
      
      // ✅ YENİ: Müşteri Ekranı (Customer Display) ayarlarını kaydet
      try {
        const cdResult = await saveCustomerDisplaySettingsToApi(
          screenIdAsNumber,
          customerDisplaySettings
        );
        if (cdResult.success && cdResult.settingsId) {
          setCustomerDisplaySettings(prev => ({ ...prev, settingsId: cdResult.settingsId }));
          setInitialCustomerDisplaySettings({ ...customerDisplaySettings, settingsId: cdResult.settingsId });
          console.log("📺 [CUSTOMER DISPLAY] Ayarlar kaydedildi");
        } else {
          console.warn("⚠️ [CUSTOMER DISPLAY] Ayarlar kaydedilemedi:", cdResult.message);
        }
      } catch (cdErr) {
        console.error("❌ [CUSTOMER DISPLAY] Kaydetme hatası:", cdErr);
      }
      
      setClubEntryChanged(false);
      onClose();
    } catch (err) {
      console.error("❌ Ayarlar kaydedilirken hata:", err);
      setError(`Ayarlar kaydedilirken bir hata oluştu: ${err instanceof Error ? err.message : 'Bilinmeyen hata'}`);
    } finally {
      setSaving(false);
    }
  };
  
  const hasChangesComparedToSQL = 
    JSON.stringify(paymentOptions) !== JSON.stringify(initialPaymentOptions) ||
    JSON.stringify(selectedPersonnelIds.sort()) !== JSON.stringify(initialSelectedPersonnelIds.sort()) ||
    JSON.stringify(selectedWarehouseIds.sort()) !== JSON.stringify(initialSelectedWarehouseIds.sort()) ||
    JSON.stringify(selectedCategoriesPerWarehouse) !== JSON.stringify(initialSelectedCategoriesPerWarehouse) ||
    JSON.stringify(selectedProductsPerCategory) !== JSON.stringify(initialSelectedProductsPerCategory) ||
    JSON.stringify(definedPrinters) !== JSON.stringify(initialDefinedPrinters) ||
    JSON.stringify(categoryPrinterAssignments) !== JSON.stringify(initialCategoryPrinterAssignments) ||
    JSON.stringify(categoryPrinterAssignments2) !== JSON.stringify(initialCategoryPrinterAssignments2) ||
    printingMode !== initialPrintingMode ||
    JSON.stringify(defaultReceiptPrinter) !== JSON.stringify(initialDefaultReceiptPrinter) ||
    JSON.stringify(personnelPermissionsMap) !== JSON.stringify(initialPersonnelPermissions) ||
    preventNegativeStock !== initialPreventNegativeStock ||
    usePackagePrices !== initialUsePackagePrices || // ✅ YENİ: Şube paket fiyatı kullan
    prepaidSystemEnabled !== initialPrepaidSystemEnabled ||
    JSON.stringify(receiptSettings) !== JSON.stringify(initialReceiptDisplaySettings || {
        companyName: 'AQUASOFT',
        companyAddress: '',
        taxNumber: '',
        showReceiptHeader: true,
        showVat: true,
        showDiscountInfo: true,
        showCashierName: true,
        showReceiptNumber: false,
        receiptNumberResetValue: 999999,
        resetAtEndOfDay: false,
        printCopyCount: 1,
        productGrouping: '',
        standardFooterNote: 'Bizi tercih ettiğiniz için teşekkür ederiz.',
        thankYouMessage: 'İyi günler!',
        receiptWidth: 80,
        characterLength: 48,
        marginTop: 5,
        marginBottom: 5,
        marginLeft: 5,
        marginRight: 5,
    }) ||
    useFiscalDeviceForCash !== initialUseFiscalDeviceForCash ||
    selectedFiscalDevice !== initialSelectedFiscalDevice || // YENİ: Yazarkasa markası kontrolü
    warnIfFiscalNotConnected !== initialWarnIfFiscalNotConnected || // ✅ YENİ: Yazarkasa bağlantı uyarısı kontrolü
    usePavoPayForOtherPayments !== initialUsePavoPayForOtherPayments ||
    useExternalPrinterForPavo !== initialUseExternalPrinterForPavo ||
    pavoPaymentMediatorId !== initialPavoPaymentMediatorId ||
    useRestP2PForPayments !== initialUseRestP2PForPayments || // ✅ YENİ
    pavoRestP2PDeviceScreenCode !== initialPavoRestP2PDeviceScreenCode || // ✅ YENİ
    pavoRestP2PDeviceName !== initialPavoRestP2PDeviceName || // ✅ YENİ
    allowAccountCreationOnSale !== initialAllowAccountCreationOnSale ||
    allowAccountCollection !== initialAllowAccountCollection ||
    showCashChangeModal !== initialShowCashChangeModal || // YENİ: Para üstü modalı kontrolü
    useScale !== initialUseScale || // YENİ: Tartı ayarları
    selectedScaleId !== initialSelectedScaleId ||
    scaleReadMode !== initialScaleReadMode ||
    readWeightBarcode !== initialReadWeightBarcode ||
    useKitchenScreen !== initialUseKitchenScreen ||
    showScreenLayoutButton !== initialShowScreenLayoutButton ||
    selectedQRMenuId !== initialSelectedQRMenuId ||
    useCashierSystem !== initialUseCashierSystem || // YENİ: Kasiyer sistemi kontrolü
    autoLockEnabled !== initialAutoLockEnabled ||
    autoLockTimeoutSeconds !== initialAutoLockTimeoutSeconds ||
    JSON.stringify(predefinedDeleteReasons) !== JSON.stringify(initialPredefinedDeleteReasons) ||
    JSON.stringify(predefinedItemNotes) !== JSON.stringify(initialPredefinedItemNotes) ||
    localServiceUrl !== initialLocalServiceUrl || // YENİ: Yerel servis URL kontrolü
    JSON.stringify(adisyonPrinter) !== JSON.stringify(initialAdisyonPrinter) || // YENİ: Adisyon yazıcısı kontrolü
    JSON.stringify(uncategorizedPrinter) !== JSON.stringify(initialUncategorizedPrinter) || // YENİ: Kategorisiz ürün yazıcısı kontrolü
    useBothFiscalAndReceiptPrinter !== initialUseBothFiscalAndReceiptPrinter || // YENİ: Yazarkasa+Fiş kontrolü
    // ✅ YENİ: Müşteri Ekranı değişiklik kontrolü (images hariç - çünkü onlar anında kaydediliyor)
    customerDisplaySettings.isEnabled !== initialCustomerDisplaySettings.isEnabled ||
    customerDisplaySettings.animationType !== initialCustomerDisplaySettings.animationType ||
    customerDisplaySettings.slideInterval !== initialCustomerDisplaySettings.slideInterval ||
    customerDisplaySettings.showCartOnActivity !== initialCustomerDisplaySettings.showCartOnActivity ||
    customerDisplaySettings.cartDisplayDuration !== initialCustomerDisplaySettings.cartDisplayDuration ||
    customerDisplaySettings.cartBackgroundColor !== initialCustomerDisplaySettings.cartBackgroundColor ||
    customerDisplaySettings.cartTextColor !== initialCustomerDisplaySettings.cartTextColor ||
    customerDisplaySettings.cartTotalFontSize !== initialCustomerDisplaySettings.cartTotalFontSize ||
    customerDisplaySettings.cartSideImageUrl !== initialCustomerDisplaySettings.cartSideImageUrl || // ✅ YENİ
    // ✅ YENİ: Entegrasyon görünürlük ayarları (Paket modu için)
    (isPackageMode && JSON.stringify(integrationSettings) !== JSON.stringify(initialIntegrationSettings)) ||
    clubEntryChanged;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log('═══════════════════════════════════════════');
    console.log('🔵 [TAB CHANGE] Yeni tab değeri:', newValue);
    console.log('🔵 [TAB CHANGE] isRestaurantMode:', isRestaurantMode);
    console.log('🔵 [TAB CHANGE] Mevcut activeTab:', activeTab);
    
    // Tab isimlerini göster
    // Aşağıdaki agentTabs ve render tabs dizileriyle aynı sırada tutulmalı.
    const tabNames = isRestaurantMode 
      ? ['Masa Grupları', 'Masalar', 'Ödeme', 'Login İşlemleri', 'Garson Atama', 'Kasiyer Atama', 'Depo', 'Yazdırma', 'Ek Ayarlar', 'Yerel Servis', 'Müşteri Ekranı', 'Cihazlar', 'Kiosk Cihazları', 'Ürün Açıklamaları']
      : ['Ödeme', 'Login İşlemleri', 'Kasiyer Atama', 'Depo', 'Yazdırma', 'Ek Ayarlar', 'Yerel Servis', 'Müşteri Ekranı', 'Kulüp Girişi', 'Entegrasyonlar', 'Cihazlar', 'Kiosk Cihazları', 'Ürün Açıklamaları'];
    console.log('🔵 [TAB CHANGE] Seçilen tab:', tabNames[newValue] || 'Bilinmeyen');
    console.log('═══════════════════════════════════════════');
    
    setActiveTab(newValue);
  };

  const handlePaymentOptionFieldChange = (id: string, field: keyof PaymentOption, value: any) => {
    setPaymentOptions(prevOptions =>
      prevOptions.map(opt =>
        opt.id === id ? { ...opt, [field]: value } : opt
      )
    );
  };

  // Ödeme sekmesindeki "+" ile oluşturulan kasa/banka kaydını listelere ekler.
  // Mevcut kayıt seçildiğinde (aynı isim) liste değişmez.
  const handleQuickCreatedCashbox = (cashbox: CreatedCashbox) => {
    setAllCashboxes(prev =>
      prev.some(cb => cb.id === cashbox.id) ? prev : [...prev, cashbox]
    );
  };

  const handleQuickCreatedBankAccount = (bankAccount: CreatedBankAccount) => {
    setAllBankAccounts(prev =>
      prev.some(ba => ba.id === bankAccount.id)
        ? prev
        : [
            ...prev,
            {
              id: bankAccount.id,
              bankName: bankAccount.bankName,
              accountNumber: bankAccount.accountNumber,
              isPosAccount: true,
            },
          ]
    );
  };

  // ==========================================================================
  // AI AJAN KÖPRÜSÜ — sekme listesi (aşağıdaki JSX'teki tab tanımlarıyla aynı
  // sırada tutulmalı; value = activeTab değeri) + modal API kaydı.
  // ==========================================================================
  const agentTabs = useMemo(() => {
    if (isRestaurantMode) {
      return [
        { value: 0, label: t('quickSaleSettingsModal.tabs.tableGroups') },
        { value: 1, label: t('quickSaleSettingsModal.tabs.tables') },
        { value: 2, label: t('quickSaleSettingsModal.tabs.paymentMethods') },
        { value: 3, label: t('quickSaleSettingsModal.tabs.personnelAssignment') },
        { value: 4, label: t('quickSaleSettingsModal.tabs.waiterAssignment') },
        { value: 5, label: t('quickSaleSettingsModal.tabs.cashierAssignment') },
        { value: 6, label: t('quickSaleSettingsModal.tabs.warehouseStock') },
        { value: 7, label: t('quickSaleSettingsModal.tabs.printing') },
        { value: 8, label: t('quickSaleSettingsModal.tabs.extraSettings') },
        { value: 9, label: t('quickSaleSettingsModal.tabs.localService') },
        { value: 10, label: t('quickSaleSettingsModal.tabs.customerDisplay') },
        { value: 11, label: t('quickSaleSettingsModal.tabs.pavoDevices') },
        { value: 12, label: t('quickSaleSettingsModal.tabs.kioskDevices') },
        { value: 13, label: t('quickSaleSettingsModal.tabs.itemNotes') },
      ];
    }
    return [
      { value: 0, label: t('quickSaleSettingsModal.tabs.paymentMethods') },
      { value: 1, label: t('quickSaleSettingsModal.tabs.personnelAssignment') },
      { value: 2, label: t('quickSaleSettingsModal.tabs.cashierAssignment') },
      { value: 3, label: t('quickSaleSettingsModal.tabs.warehouseStock') },
      { value: 4, label: t('quickSaleSettingsModal.tabs.printing') },
      { value: 5, label: t('quickSaleSettingsModal.tabs.extraSettings') },
      { value: 6, label: t('quickSaleSettingsModal.tabs.localService') },
      { value: 7, label: t('quickSaleSettingsModal.tabs.customerDisplay') },
      { value: 8, label: t('quickSaleSettingsModal.tabs.clubEntry') },
      ...(isPackageMode ? [{ value: 9, label: t('quickSaleSettingsModal.tabs.integrations') }] : []),
      { value: 10, label: t('quickSaleSettingsModal.tabs.pavoDevices') },
      { value: 11, label: t('quickSaleSettingsModal.tabs.kioskDevices') },
      { value: 12, label: t('quickSaleSettingsModal.tabs.itemNotes') },
    ];
  }, [t, isRestaurantMode, isPackageMode]);

  // ── AI köprüsü yardımcıları ──────────────────────────────────────────────
  // Kasa + banka listeleri normalde Ödeme Yöntemleri sekmesi açılınca lazy
  // yüklenir; ajan sekmeye girmeden ödeme yöntemi ekleyebilsin diye burada da
  // (boşsa) aynı kaynaktan yüklenir. Mapping useEffect'teki ile birebir aynı.
  const ensurePaymentBindingDataForAgent = async (): Promise<{
    cashboxes: Cashbox[];
    bankAccounts: BankAccount[];
  }> => {
    let cashboxes = allCashboxes;
    if (cashboxes.length === 0) {
      const cashboxData = await accountingService.getCashboxes();
      cashboxes = (Array.isArray(cashboxData) ? cashboxData : [])
        .map((c: any) => ({
          id: (c?.Id || c?.id)?.toString() || '',
          name: c?.Name || c?.name || 'İsimsiz Kasa',
        }))
        .filter((c) => c.id);
      setAllCashboxes(cashboxes);
    }
    let bankAccounts = allBankAccounts;
    if (bankAccounts.length === 0) {
      const bankData = await accountingService.getBankAccounts();
      bankAccounts = (Array.isArray(bankData) ? bankData : [])
        .map((data: any) => {
          const subAccountsObj: { [key: string]: SubAccountForModal } = {};
          const subAccountsArray = data.subAccounts || data.SubAccounts;
          if (Array.isArray(subAccountsArray)) {
            subAccountsArray.forEach((subAccount: any) => {
              const subId = (subAccount.id || subAccount.Id)?.toString();
              if (subId) {
                subAccountsObj[subId] = {
                  id: subId,
                  accountName: subAccount.accountName || subAccount.AccountName || 'İsimsiz Alt Hesap',
                  accountNumber: subAccount.accountNumber || subAccount.AccountNumber || '-',
                  isPosAccount: subAccount.isPosAccount || subAccount.IsPosAccount || false,
                };
              }
            });
          }
          return {
            id: ((data.id || data.Id)?.toString()) || '',
            bankName: data.bankName || data.BankName || 'İsimsiz Banka',
            accountNumber: data.accountNumber || data.AccountNumber || '-',
            isPosAccount: data.isPosAccount || data.IsPosAccount || false,
            subAccounts: Object.keys(subAccountsObj).length > 0 ? subAccountsObj : undefined,
          };
        })
        .filter((b: BankAccount) => b.id);
      setAllBankAccounts(bankAccounts);
    }
    return { cashboxes, bankAccounts };
  };

  /** AI köprüsü: ödeme yöntemini bağlantı alanlarıyla tek adımda ekler (handleAddPaymentOption deseni). */
  const addPaymentOptionForAgent = (
    type: PaymentOptionType,
    fields: Record<string, any>
  ): { id: string; label: string } => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    const newOption: PaymentOption = {
      id: tempId,
      type,
      label: '',
      enabled: true,
      cashboxId: null,
      bankAccountId: null,
      defaultAccountId: null,
      subAccountId: null,
      accountId: null,
      accountName: null,
      onlineCashboxId: null,
      onlineCashboxName: null,
      onlineBankAccountId: null,
      onlineBankAccountName: null,
      onlineSubAccountId: null,
      ...fields,
    };
    setPaymentOptions(prev => [...prev, newOption]);
    setError(null);
    return { id: newOption.id, label: newOption.label };
  };

  /**
   * Depo/Stok taslağını HEDEF DEPOYA uygular (taslak bandı + AI köprüsü ortak yolu).
   *
   * Taslağın kendi deposu KULLANILMAZ: taslak, kaydedildiği ekranın depo
   * kimlikleriyle anahtarlanmış maplar tutar ama hedef ekranda o depo anlamlı
   * olmayabilir. Bu yüzden seçimler depodan bağımsız hale getirilip
   * (flattenTemplateSelection) kullanıcının seçtiği deponun altına yazılır.
   *
   * Hedef depodaki mevcut seçimler SİLİNMEZ, birleştirilir — taslak "ekler",
   * "sıfırlayıp yazmaz".
   */
  const applyWarehouseTemplateToWarehouse = (
    template: WarehouseStockTemplate,
    targetWarehouseId: string
  ) => {
    if (!targetWarehouseId) return;
    const { categoryIds, productIdsByCategory } = flattenTemplateSelection(template);

    const nextWarehouseIds = selectedWarehouseIds.includes(targetWarehouseId)
      ? selectedWarehouseIds
      : [...selectedWarehouseIds, targetWarehouseId];
    setSelectedWarehouseIds(nextWarehouseIds);

    setSelectedCategoriesPerWarehouse(prev => {
      const current = prev[targetWarehouseId] || [];
      const merged = [...current, ...categoryIds.filter(id => !current.includes(id))];
      return { ...prev, [targetWarehouseId]: merged };
    });

    setSelectedProductsPerCategory(prev => {
      const currentForWarehouse = prev[targetWarehouseId] || {};
      const mergedForWarehouse = { ...currentForWarehouse };
      Object.entries(productIdsByCategory).forEach(([categoryId, productIds]) => {
        const current = mergedForWarehouse[categoryId] || [];
        mergedForWarehouse[categoryId] = [
          ...current,
          ...productIds.filter(id => !current.includes(id)),
        ];
      });
      return { ...prev, [targetWarehouseId]: mergedForWarehouse };
    });

    // Depo sekmelerinin sırası allWarehouses'a göre süzülüyor (WarehouseStockSettingsTab),
    // bu yüzden aktif sekme indexi de aynı sıradan hesaplanıyor.
    const targetIndex = allWarehouses
      .filter(w => nextWarehouseIds.includes(w.id))
      .findIndex(w => w.id === targetWarehouseId);
    setActiveWarehouseSubTab(targetIndex >= 0 ? targetIndex : 0);
    setActiveCategorySubTabPerWarehouse(prev => ({ ...prev, [targetWarehouseId]: 0 }));

    if (typeof template.usePackagePrices === 'boolean') {
      setUsePackagePrices(template.usePackagePrices);
    }
  };

  /** AI köprüsü: QR menü tanımları (QRMenuSelectionModal ile aynı uç). */
  const listQrMenusForAgent = async (): Promise<Array<{ id: string; name: string; branchName?: string }>> => {
    const response = await mainApi.get(`/api/QRMenu/menu-definitions`);
    const result = response.data;
    if (!result?.success || !Array.isArray(result.data)) return [];
    return result.data.map((menu: any) => ({
      id: String(menu.id),
      name: menu.name || 'İsimsiz Menü',
      branchName: menu.branchName || undefined,
    }));
  };

  useQuickSaleSettingsAgentBridge({
    enabled: open && !loading,
    screenName: screenName || '',
    isRestaurantMode,
    isPackageMode,
    tabs: agentTabs,
    activeTab,
    setActiveTab,
    paymentOptions,
    setPaymentOptionEnabled: (id, enabled) =>
      handlePaymentOptionFieldChange(id, 'enabled', enabled),
    ensurePaymentBindingData: ensurePaymentBindingDataForAgent,
    addPaymentOptionWithFields: addPaymentOptionForAgent,
    // handleDeletePaymentOption bu satırdan sonra tanımlanır — arrow ile sarılır (TDZ)
    removePaymentOptionById: (id) => handleDeletePaymentOption(id),
    allPersonnel,
    selectedPersonnelIds,
    setSelectedPersonnelIds,
    selectedQrMenuName: selectedQRMenuName || null,
    listQrMenus: listQrMenusForAgent,
    setKitchenQrMenu: (menu) => {
      setSelectedQRMenuId(menu?.id ?? null);
      setSelectedQRMenuName(menu?.name ?? '');
    },
    useCashierSystem,
    setUseCashierSystem,
    autoLockEnabled,
    setAutoLockEnabled,
    autoLockTimeoutSeconds,
    setAutoLockTimeoutSeconds,
    allWarehouses,
    selectedWarehouseIds,
    setSelectedWarehouseIds,
    preventNegativeStock,
    setPreventNegativeStock,
    // Depo/Stok taslakları — WarehouseStockSettingsTab'daki taslak sistemiyle aynı
    // localStorage kaynağı ve uygulama akışı (onApplyWarehouseTemplate ile birebir)
    listWarehouseTemplates: () => loadWarehouseStockTemplates(companyId),
    applyWarehouseTemplate: applyWarehouseTemplateToWarehouse,
    saveWarehouseTemplate: (name: string) =>
      saveWarehouseStockTemplate(companyId, {
        name,
        selectedWarehouseIds,
        selectedCategoriesPerWarehouse,
        selectedProductsPerCategory,
        usePackagePrices,
      }),
    printingMode,
    setPrintingMode,
    // Ada göre yazıcı çözümü için tanımlı + yerel yazıcılar birleştirilir
    availablePrinters: [
      ...definedPrinters,
      ...localPrinters.filter((lp) => !definedPrinters.some((dp) => dp.name === lp.name)),
    ],
    defaultReceiptPrinterName: defaultReceiptPrinter?.name ?? null,
    setDefaultReceiptPrinterByName: (printer) => setDefaultReceiptPrinter(printer),
    prepaidSystemEnabled,
    setPrepaidSystemEnabled,
    showProductImages,
    setShowProductImages,
    showCashChangeModal,
    setShowCashChangeModal,
    allowAccountCreationOnSale,
    setAllowAccountCreationOnSale,
    useKitchenScreen,
    setUseKitchenScreen,
    showScreenLayoutButton,
    setShowScreenLayoutButton,
    warnIfFiscalNotConnected,
    setWarnIfFiscalNotConnected,
    useFiscalDeviceForCash,
    setUseFiscalDeviceForCash,
    localServiceUrl,
    setLocalServiceUrl,
    hasChanges: hasChangesComparedToSQL,
    saving,
    error,
    save: handleSave,
    close: onClose,
  });

  const handleAddPaymentOption = (type: PaymentOptionType) => {
    let defaultLabel = '';
    switch (type) {
      case 'cash': defaultLabel = 'Nakit'; break;
      case 'pos': defaultLabel = 'Yeni POS'; break;
      case 'openAccount': defaultLabel = 'Açık Hesap'; break;
      case 'other': defaultLabel = ''; break; // Kullanıcı dropdown'dan seçecek
      case 'online': defaultLabel = ''; break; // ✅ YENİ: Kullanıcı dropdown'dan seçecek
      default: defaultLabel = 'Yeni Ödeme Yöntemi';
    }

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

    const newOption: PaymentOption = {
      id: tempId,
      type: type,
      label: defaultLabel,
      enabled: true,
      cashboxId: null,
      bankAccountId: null,
      defaultAccountId: null,
      subAccountId: null, // Açıkça null olarak tanımla
      accountId: null, // ✅ Diğer ödeme için cari hesap ID
      accountName: null, // ✅ Diğer ödeme için cari hesap adı
      // ✅ YENİ: Online ödeme için ek alanlar
      onlineCashboxId: null,
      onlineCashboxName: null,
      onlineBankAccountId: null,
      onlineBankAccountName: null,
      onlineSubAccountId: null,
    };
    setPaymentOptions(prev => [...prev, newOption]);
    setError(null);
  };

  const handleDeletePaymentOption = (idToDelete: string) => {
      setPaymentOptions(prev => prev.filter(opt => opt.id !== idToDelete));
  };

  const handleActiveWarehouseSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveWarehouseSubTab(newValue);
    const selectedWarehouses = allWarehouses.filter(w => selectedWarehouseIds.includes(w.id));
    const activeWarehouse = selectedWarehouses[newValue];
    if (activeWarehouse && activeCategorySubTabPerWarehouse[activeWarehouse.id] === undefined) {
        setActiveCategorySubTabPerWarehouse(prev => ({...prev, [activeWarehouse.id]: 0}));
    }
  };

  const handleRemoveWarehouse = (event: React.MouseEvent, warehouseIdToRemove: string) => {
    event.stopPropagation();

    const newSelectedWarehouseIds = selectedWarehouseIds.filter(id => id !== warehouseIdToRemove);
    setSelectedWarehouseIds(newSelectedWarehouseIds);

    setSelectedCategoriesPerWarehouse(prev => {
        const newState = { ...prev };
        delete newState[warehouseIdToRemove];
        return newState;
    });
    
    const currentActiveIndex = selectedWarehouseIds.indexOf(warehouseIdToRemove);
    if (activeWarehouseSubTab >= newSelectedWarehouseIds.length) {
        setActiveWarehouseSubTab(Math.max(0, newSelectedWarehouseIds.length - 1));
    } else if (activeWarehouseSubTab === currentActiveIndex && currentActiveIndex === newSelectedWarehouseIds.length) {
         setActiveWarehouseSubTab(Math.max(0, newSelectedWarehouseIds.length - 1));
    }
  };

  /**
   * Bir depo sekmesinin kategori/ürün seçimlerini başka depoya taşır.
   *
   * Yanlış depo seçildiğinde eski çözüm "depoyu kaldır + yenisini ekle"ydi; bu da
   * onlarca kategori ve ürünü elle yeniden seçmek demekti. Burada yalnızca
   * seçimlerin anahtarı değişiyor, ekrandaki sekme sırası korunuyor.
   *
   * Hedef depoda ürünlerin stok tanımı olup olmadığını WarehouseChangeDialog
   * kontrol eder (taslak uygulama akışıyla aynı yol).
   */
  const handleChangeWarehouse = (sourceWarehouseId: string, targetWarehouseId: string) => {
    if (!sourceWarehouseId || !targetWarehouseId || sourceWarehouseId === targetWarehouseId) return;

    const nextWarehouseIds = selectedWarehouseIds.map(id =>
      id === sourceWarehouseId ? targetWarehouseId : id
    );
    setSelectedWarehouseIds(nextWarehouseIds);

    setSelectedCategoriesPerWarehouse(prev => {
      const next = { ...prev };
      const moved = next[sourceWarehouseId] || [];
      delete next[sourceWarehouseId];
      // Hedef depo normalde ekranda seçili değil (diyalog seçilileri listelemez),
      // yine de olası bir çakışmada birleştiriyoruz.
      const existing = next[targetWarehouseId] || [];
      next[targetWarehouseId] = [...existing, ...moved.filter(id => !existing.includes(id))];
      return next;
    });

    setSelectedProductsPerCategory(prev => {
      const next = { ...prev };
      const moved = next[sourceWarehouseId] || {};
      delete next[sourceWarehouseId];
      const merged = { ...(next[targetWarehouseId] || {}) };
      Object.entries(moved).forEach(([categoryId, productIds]) => {
        const current = merged[categoryId] || [];
        merged[categoryId] = [...current, ...productIds.filter(id => !current.includes(id))];
      });
      next[targetWarehouseId] = merged;
      return next;
    });

    setActiveCategorySubTabPerWarehouse(prev => {
      const next = { ...prev };
      const moved = next[sourceWarehouseId] ?? 0;
      delete next[sourceWarehouseId];
      next[targetWarehouseId] = moved;
      return next;
    });

    // Depo sekmeleri allWarehouses sırasına göre çiziliyor; aktif sekmeyi yeni
    // deponun o sıradaki yerine taşıyoruz.
    const targetIndex = allWarehouses
      .filter(w => nextWarehouseIds.includes(w.id))
      .findIndex(w => w.id === targetWarehouseId);
    setActiveWarehouseSubTab(targetIndex >= 0 ? targetIndex : 0);

    console.log(`🔄 [DEPO DEĞİŞTİR] ${sourceWarehouseId} → ${targetWarehouseId}`);
  };

  const handleRemoveCategory = (event: React.MouseEvent, warehouseId: string, categoryIdToRemove: string) => {
    event.stopPropagation();

    const currentCategoriesForWarehouse = selectedCategoriesPerWarehouse[warehouseId] || [];
    const removedCategoryIndex = currentCategoriesForWarehouse.indexOf(categoryIdToRemove);
    const newCategoriesForWarehouse = currentCategoriesForWarehouse.filter(id => id !== categoryIdToRemove);
    const currentActiveCategoryTabIndex = activeCategorySubTabPerWarehouse[warehouseId] || 0;
    const newCategoriesLength = newCategoriesForWarehouse.length;

    setSelectedCategoriesPerWarehouse(prev => ({
        ...prev,
        [warehouseId]: newCategoriesForWarehouse,
    }));

    setSelectedProductsPerCategory(prevProducts => {
      const warehouseProducts = { ...(prevProducts[warehouseId] || {}) };
      delete warehouseProducts[categoryIdToRemove];
      return {
        ...prevProducts,
        [warehouseId]: warehouseProducts,
      };
    });

    if (newCategoriesLength <= 0) {
        setActiveCategorySubTabPerWarehouse(prev => ({ ...prev, [warehouseId]: 0 }));
    } else if (currentActiveCategoryTabIndex >= newCategoriesLength) {
        setActiveCategorySubTabPerWarehouse(prev => ({ ...prev, [warehouseId]: Math.max(0, newCategoriesLength - 1) }));
    } else if (currentActiveCategoryTabIndex === removedCategoryIndex && removedCategoryIndex === newCategoriesLength) {
         setActiveCategorySubTabPerWarehouse(prev => ({ ...prev, [warehouseId]: Math.max(0, newCategoriesLength - 1) }));
    }
  };

  // ✅ YENİ: Kategori sırasını değiştir (QR Menü gibi)
  const handleMoveCategory = (warehouseId: string, categoryId: string, direction: 'left' | 'right') => {
    const currentCategories = selectedCategoriesPerWarehouse[warehouseId] || [];
    const currentIndex = currentCategories.indexOf(categoryId);
    
    if (currentIndex === -1) return;
    
    if (direction === 'left' && currentIndex > 0) {
      const newCategories = [...currentCategories];
      [newCategories[currentIndex - 1], newCategories[currentIndex]] = [newCategories[currentIndex], newCategories[currentIndex - 1]];
      
      setSelectedCategoriesPerWarehouse(prev => ({
        ...prev,
        [warehouseId]: newCategories
      }));
      
      // Aktif tab'ı da güncelle
      const currentActiveTab = activeCategorySubTabPerWarehouse[warehouseId] || 0;
      if (currentActiveTab === currentIndex) {
        setActiveCategorySubTabPerWarehouse(prev => ({
          ...prev,
          [warehouseId]: currentIndex - 1
        }));
      } else if (currentActiveTab === currentIndex - 1) {
        setActiveCategorySubTabPerWarehouse(prev => ({
          ...prev,
          [warehouseId]: currentIndex
        }));
      }
      
      console.log(`⬅️ [CATEGORY ORDER] Kategori sola kaydırıldı: ${categoryId}`);
    } else if (direction === 'right' && currentIndex < currentCategories.length - 1) {
      const newCategories = [...currentCategories];
      [newCategories[currentIndex + 1], newCategories[currentIndex]] = [newCategories[currentIndex], newCategories[currentIndex + 1]];
      
      setSelectedCategoriesPerWarehouse(prev => ({
        ...prev,
        [warehouseId]: newCategories
      }));
      
      // Aktif tab'ı da güncelle
      const currentActiveTab = activeCategorySubTabPerWarehouse[warehouseId] || 0;
      if (currentActiveTab === currentIndex) {
        setActiveCategorySubTabPerWarehouse(prev => ({
          ...prev,
          [warehouseId]: currentIndex + 1
        }));
      } else if (currentActiveTab === currentIndex + 1) {
        setActiveCategorySubTabPerWarehouse(prev => ({
          ...prev,
          [warehouseId]: currentIndex
        }));
      }
      
      console.log(`➡️ [CATEGORY ORDER] Kategori sağa kaydırıldı: ${categoryId}`);
    }
  };

  // ✅ YENİ: Ürünü kategoriden kaldır
  const handleRemoveProduct = (warehouseId: string, categoryId: string, productIdToRemove: string) => {
    setSelectedProductsPerCategory(prevProducts => {
      const warehouseProducts = { ...(prevProducts[warehouseId] || {}) };
      const categoryProducts = [...(warehouseProducts[categoryId] || [])];
      const updatedCategoryProducts = categoryProducts.filter(id => id !== productIdToRemove);
      
      return {
        ...prevProducts,
        [warehouseId]: {
          ...warehouseProducts,
          [categoryId]: updatedCategoryProducts
        }
      };
    });

    // Ürün detaylarından da kaldır (opsiyonel - memory tasarrufu için)
    setSelectedProductsDetails(prev => {
      const newDetails = { ...prev };
      delete newDetails[productIdToRemove];
      return newDetails;
    });

    console.log(`✅ Ürün kaldırıldı: ${productIdToRemove}`);
  };

  const handleActiveCategorySubTabChange = (warehouseId: string, newValue: number) => {
    setActiveCategorySubTabPerWarehouse(prev => ({
      ...prev,
      [warehouseId]: newValue,
    }));
  };

  const handleWarehouseSelectionSave = (newSelectedIds: string[]) => {
    setSelectedWarehouseIds(newSelectedIds);
    setIsWarehouseSelectionModalOpen(false);
    if (newSelectedIds.length === 0 || activeWarehouseSubTab >= newSelectedIds.length) {
        setActiveWarehouseSubTab(0);
    }
  };

  // Ürün kategorileri modalındaki "Ekle" butonu: kategoriyi hedef deponun satış
  // listesine ekler. Modal açık kalır, kullanıcı sırayla birkaç kategori ekleyebilir.
  const handleAddCategoryToWarehouse = (categoryId: string, categoryName: string) => {
    const warehouseId = currentWarehouseIdForCategoryModal;
    if (!warehouseId) return;

    // Kategori modal içinde yeni oluşturulmuş olabilir; listede yoksa sekme adı
    // çözümlenemeyeceği için burada iyimser olarak ekliyoruz. Modal kapanışındaki
    // yeniden yükleme zaten sunucu halini yazacak.
    setAllCategories(prev =>
      prev.some(c => c.id === categoryId)
        ? prev
        : [...prev, { id: categoryId, name: categoryName, parentId: null, level: 1, createdAt: '', branchesId: null }]
    );

    setSelectedCategoriesPerWarehouse(prev => {
      const existing = prev[warehouseId] || [];
      if (existing.includes(categoryId)) return prev;
      return { ...prev, [warehouseId]: [...existing, categoryId] };
    });
  };

  const handleOpenInventoryCategoryModal = (warehouseId?: string) => {
    setCurrentWarehouseIdForCategoryModal(warehouseId ?? null);
    setIsInventoryCategoryModalOpen(true);
  };

  const handleCloseInventoryCategoryModal = async () => {
    // Hedef depo id'si burada sıfırlanmıyor: kapanış animasyonu sürerken
    // "Satış" sütununun kaybolup görünmesini engelliyor. Açılışta yeniden set edilir.
    setIsInventoryCategoryModalOpen(false);
    if (companyId) {
      // Kategorileri SQL'den yeniden yükle
      try {
        const categoriesData = await accountingService.getCategories();
        if (Array.isArray(categoriesData)) {
          const categoryList = mapInventoryCategories(categoriesData);
          // Liste beklenmedik şekilde boş dönerse mevcut listeyi KORU: aksi halde
          // Depo Stok sekmesi seçili kategori/ürünleri çizemez ve kullanıcı ayarları
          // kaybettiğini sanır (state duruyor, yalnızca ad çözümlemesi kopuyor).
          if (categoryList.length > 0) {
            setAllCategories(categoryList);
            console.log(`✅ ${categoryList.length} kategori SQL'den yeniden yüklendi`);
          } else {
            console.warn('⚠️ Kategori listesi boş döndü, mevcut liste korunuyor');
          }
        }
      } catch (err) {
        console.error("❌ Kategori listesi yeniden yüklenemedi:", err);
      }
    }
  };

  const handleOpenProductModal = (warehouseId: string, categoryId: string) => {
    setCurrentWarehouseIdForProductModal(warehouseId);
    setCurrentCategoryIdForProductModal(categoryId);
    setIsProductSelectionModalOpen(true);
  };

  const handleProductSelectionSave = (selectedProductIds: string[], selectedProductsData: any[]) => {
    if (currentWarehouseIdForProductModal && currentCategoryIdForProductModal) {
        // ✅ FIX: Yeni eklenen ürünleri diğer kategorilerden çıkar (çakışma önleme)
        setSelectedProductsPerCategory(prev => {
            const newState = { ...prev };
            
            // Yeni seçilen ürün ID'leri - bu ürünler sadece bu kategoride olmalı
            const newProductIdSet = new Set(selectedProductIds);
            
            // Tüm depolar ve kategorileri tara
            Object.keys(newState).forEach(warehouseId => {
                if (newState[warehouseId]) {
                    Object.keys(newState[warehouseId]).forEach(categoryId => {
                        // Mevcut kategori değilse, çakışan ürünleri çıkar
                        if (warehouseId !== currentWarehouseIdForProductModal || categoryId !== currentCategoryIdForProductModal) {
                            const existingProducts = newState[warehouseId][categoryId] || [];
                            const filteredProducts = existingProducts.filter(id => !newProductIdSet.has(id));
                            
                            // Çıkarılan ürün varsa logla
                            const removedCount = existingProducts.length - filteredProducts.length;
                            if (removedCount > 0) {
                                console.log(`🔄 [PRODUCT SELECTION] ${removedCount} ürün "${categoryId}" kategorisinden çıkarıldı (artık "${currentCategoryIdForProductModal}" kategorisinde)`);
                            }
                            
                            newState[warehouseId][categoryId] = filteredProducts;
                        }
                    });
                }
            });
            
            // Mevcut kategoriyi güncelle
            newState[currentWarehouseIdForProductModal!] = {
                ...(newState[currentWarehouseIdForProductModal!] || {}),
                [currentCategoryIdForProductModal!]: selectedProductIds,
            };
            
            return newState;
        });
        
        // Ürün detaylarını kaydet
        const newDetails: { [productId: string]: any } = {};
        selectedProductsData.forEach(product => {
          newDetails[product.id] = product;
        });
        setSelectedProductsDetails(prev => ({
          ...prev,
          ...newDetails
        }));
        
        console.log('✅ [PRODUCT SELECTION] Kaydedilen ürünler:', selectedProductsData.length);
    }
    setIsProductSelectionModalOpen(false);
    setCurrentWarehouseIdForProductModal(null);
    setCurrentCategoryIdForProductModal(null);
  };

  const handleSelectedProductsPageChange = (key: string, newPage: number) => {
    setSelectedProductsPage(prev => ({ ...prev, [key]: newPage }));
  };

  const handleSelectedProductsRowsPerPageChange = (key: string, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSelectedProductsRowsPerPage(prev => ({ ...prev, [key]: parseInt(event.target.value, 10) }));
    setSelectedProductsPage(prev => ({ ...prev, [key]: 0 }));
  };

  const handleAddPrinter = () => {
    if (newPrinterName.trim() === "") return;
    const newPrinter: DefinedPrinter = {
      id: `printer_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      name: newPrinterName.trim(),
    };
    setDefinedPrinters(prev => [...prev, newPrinter]);
    setNewPrinterName("");
  };

  const handleDeletePrinter = (printerId: string) => {
    setDefinedPrinters(prev => prev.filter(p => p.id !== printerId));
    setCategoryPrinterAssignments(prevAssignments => {
      const newAssignments = { ...prevAssignments };
      Object.keys(newAssignments).forEach(catId => {
        if (newAssignments[catId] === printerId) {
          newAssignments[catId] = null;
        }
      });
      return newAssignments;
    });
  };

  const handleCategoryPrinterChange = (categoryId: string, printerId: string | null) => {
    setCategoryPrinterAssignments(prev => ({
      ...prev,
      [categoryId]: printerId,
    }));
  };

  // ✅ YENİ: Kategori başına 2. yazıcı seçimi
  const handleCategoryPrinterChange2 = (categoryId: string, printerId: string | null) => {
    setCategoryPrinterAssignments2(prev => ({
      ...prev,
      [categoryId]: printerId,
    }));
  };

  const handleSelectPersonnelForPermissions = (personnelId: string) => {
    setSelectedPersonnelForPermissions(personnelId);
    
    if (!personnelPermissionsMap[personnelId]) {
      setPersonnelPermissionsMap(prev => ({
        ...prev,
        [personnelId]: {
          changePrice: false,
          applyDiscount: false,
          cancel: false,
          viewReports: false,
          warehouseOperations: false,
          viewAccountBalance: false,
          listAccounts: false,
          canQuickAddItem: false, // Varsayılan değer
          canAddAccount: false    // Varsayılan değer
        }
      }));
    }
  };

  const handlePermissionChange = (personnelId: string, permission: string, value: boolean) => {
    setPersonnelPermissionsMap(prev => ({
      ...prev,
      [personnelId]: {
        ...prev[personnelId],
        [permission]: value
      }
    }));
  };

  const [printTestLoading, setPrintTestLoading] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const handlePrintingModeChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    newMode: string
    ) => {
      if (newMode === 'single' || newMode === 'category') {
        setPrintingMode(newMode as 'single' | 'category');
      }
    };

    const handleSetDefaultPrinter = (printer: DefinedPrinter) => {
      setDefaultReceiptPrinter(printer);
    };

    const handleRemoveDefaultPrinter = () => {
      setDefaultReceiptPrinter(null);
    };

  const handlePrintTestPage = async (printerId: string, printerName: string) => {
    setPrintTestLoading(printerId);
    try {
      const response = await fetch('http://localhost:3003/api/sinamaSayfasiYazdir', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ printerName: printerId })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || `HTTP Hatası: ${response.status}`);
      }
      
      const result = await response.json();
      setSnackbarMessage(`'${printerName}' için sınama sayfası gönderildi.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('Sınama sayfası yazdırma hatası:', error);
      setSnackbarMessage(`Hata: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setPrintTestLoading(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const [isReceiptSettingsModalVisible, setIsReceiptSettingsModalVisible] = React.useState(false);

  const handleOpenReceiptSettingsModal = () => {
    setIsReceiptSettingsModalVisible(true);
  };

  const handleCloseReceiptSettingsModal = () => {
    setIsReceiptSettingsModalVisible(false);
  };

  const handleSaveReceiptSettings = (updatedSettings: ReceiptSettings) => {
    setReceiptSettings(updatedSettings);
    handleCloseReceiptSettingsModal();
  };

  // Yeni fiş numarası sıfırlama fonksiyonunu ekliyorum
  const handleResetReceiptNumber = () => {
    try {
      // localStorage'daki fiş numarasını 1'e sıfırla
      localStorage.setItem('currentReceiptNumber', '1');
      
      // Bugünün tarihini de kaydet (gün sonunda sıfırlama için)
      const todayDate = new Date().toISOString().split('T')[0];
      localStorage.setItem('lastReceiptDate', todayDate);
      
      // Kullanıcıya bilgi mesajı göster
      setSnackbarMessage("Fiş numarası başarıyla sıfırlandı!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Fiş numarası sıfırlanırken hata:", error);
      setSnackbarMessage("Fiş numarası sıfırlanamadı!");
      setSnackbarOpen(true);
    }
  };

  // Resim yükleme için yeni state'ler
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentProductId, setCurrentProductId] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  
  // Resim yükleme işlevi
  const handleUploadProductImage = (productId: string) => {
    if (!companyId) {
      setSnackbarMessage("Şirket ID bulunamadı. Resim yüklenemez.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    // Firebase Storage'a erişim kontrolü (rules ile ilgili uyarı)
    try {
      // Ürün ID'sini kaydet ve resim yükleme modalını aç
      setCurrentProductId(productId);
      setImageModalOpen(true);
    } catch (error) {
      console.error("Resim yükleme hazırlığı hatası:", error);
      
      // Firebase Storage kuralları ile ilgili özel mesaj
      if (error instanceof Error && error.message.includes("storage/unauthorized")) {
        setSnackbarMessage(
          "Firebase Storage erişim hatası: Storage Rules ayarlarınızı güncellemeniz gerekiyor. " +
          "Firebase konsolundan Storage > Rules bölümüne giderek read/write izinleri tanımlayın."
        );
      } else {
        setSnackbarMessage("Resim yükleme hazırlığı sırasında bir hata oluştu.");
      }
      
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };
  
  // Seçilen resmi Image Service'e yükle ve SQL envanterine kaydet
  const uploadImageToFirebase = async (file: File) => {
    try {
      if (!companyId || !currentProductId) {
        throw new Error("Şirket ID veya ürün ID bulunamadı.");
      }

      setImageUploading(true);

      // Resmi Image Service'e yükle (Firebase Storage yerine SQL/image-service yolu)
      const uploaded = await ImageUploadService.uploadImages([file]);
      const downloadURL = uploaded?.[0]?.originalUrl;
      if (!downloadURL) {
        throw new Error("Resim yüklenemedi");
      }

      // Ürünün SQL envanter kaydını güncelle.
      // Update endpoint'i tüm alanları override ettiği için önce tam item'ı GET ediyoruz,
      // sonra sadece imageUrl değişmiş halde POST update yapıyoruz.
      try {
        const detailResponse = await accountingApi.get(`/api/Inventory/items/${currentProductId}`);
        const fullItem = detailResponse.data;
        if (fullItem && fullItem.id) {
          await accountingApi.post(`/api/Inventory/items/${currentProductId}/update`, {
            ...fullItem,
            imageUrl: downloadURL,
            imageUrls: downloadURL ? downloadURL.split(',').filter(Boolean) : null,
          });
        }
      } catch (persistError) {
        // Legacy ürünler SQL'de bulunamayıp 404 dönebilir; resim yüklemesi yine de başarılı sayılır.
        console.warn("Ürün resmi SQL envantere kaydedilemedi:", persistError);
      }

      // Başarılı bildirim göster
      setSnackbarSeverity("success");
      setSnackbarMessage("Ürün resmi başarıyla yüklendi");
      setSnackbarOpen(true);
      setImageModalOpen(false);
    } catch (error) {
      console.error("Resim yükleme hatası:", error);
      setSnackbarMessage("Resim yüklenirken bir hata oluştu");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setImageUploading(false);
    }
  };

  // ⚠️ fetchProducts fonksiyonu artık gerekli değil - ürünler QuickSaleProductSelectionModal içinde lazy load ediliyor

  if (!open) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      fullScreen={isMobile}
      // AI asistanı modal açıkken de kullanılabilmeli: MUI odak tuzağı kapalı,
      // aksi halde chat input'una odak verilemiyor (odak sürekli dialog'a geri çalınır).
      disableEnforceFocus
      PaperProps={{ sx: { minHeight: isMobile ? '100vh' : '70vh' } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        bgcolor: isRestaurantMode ? 'warning.main' : 'primary.main', 
        color: 'white', 
        py: 1, 
        px: 2,
        // Mobilde daha kompakt
        minHeight: { xs: 48, md: 56 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isRestaurantMode ? <RestaurantIcon sx={{ mr: 0.8, fontSize: '1.25rem' }}/> : <SettingsIcon sx={{ mr: 0.8, fontSize: '1.25rem' }}/>}
          <Typography variant={isMobile ? 'body1' : 'subtitle1'} fontWeight="bold">
            {isRestaurantMode ? t('quickSaleSettingsModal.titles.restaurant') : t('quickSaleSettingsModal.titles.settings')}{!isMobile && `: ${screenName || screenId}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon fontSize="small"/>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Tab Listesi - Mobil: Drawer, Desktop: Tabs */}
        {(() => {
          // Tab tanımları (ikonlar ile)
          const tabs = isRestaurantMode 
            ? [
                { value: 0, label: t('quickSaleSettingsModal.tabs.tableGroups'), icon: <GroupIcon /> },
                { value: 1, label: t('quickSaleSettingsModal.tabs.tables'), icon: <TableBarIcon /> },
                { value: 2, label: t('quickSaleSettingsModal.tabs.paymentMethods'), icon: <PaymentIcon /> },
                { value: 3, label: t('quickSaleSettingsModal.tabs.personnelAssignment'), icon: <PeopleIcon /> },
                { value: 4, label: t('quickSaleSettingsModal.tabs.waiterAssignment'), icon: <RoomServiceIcon /> },
                { value: 5, label: t('quickSaleSettingsModal.tabs.cashierAssignment'), icon: <AccountBoxIcon /> },
                { value: 6, label: t('quickSaleSettingsModal.tabs.warehouseStock'), icon: <WarehouseIcon /> },
                { value: 7, label: t('quickSaleSettingsModal.tabs.printing'), icon: <PrintIcon /> },
                { value: 8, label: t('quickSaleSettingsModal.tabs.extraSettings'), icon: <SettingsIcon /> },
                { value: 9, label: t('quickSaleSettingsModal.tabs.localService'), icon: <DnsIcon /> },
                { value: 10, label: t('quickSaleSettingsModal.tabs.customerDisplay'), icon: <TvIcon /> },
                { value: 11, label: t('quickSaleSettingsModal.tabs.pavoDevices'), icon: <PointOfSaleIcon /> }, // ✅ YENİ
                { value: 12, label: t('quickSaleSettingsModal.tabs.kioskDevices'), icon: <DesktopWindowsIcon /> }, // 🖥️ Plan 2 — Kiosk Cihazlari
                { value: 13, label: t('quickSaleSettingsModal.tabs.itemNotes'), icon: <NoteAddIcon /> }
              ]
            : [
                { value: 0, label: t('quickSaleSettingsModal.tabs.paymentMethods'), icon: <PaymentIcon /> },
                { value: 1, label: t('quickSaleSettingsModal.tabs.personnelAssignment'), icon: <PeopleIcon /> },
                { value: 2, label: t('quickSaleSettingsModal.tabs.cashierAssignment'), icon: <AccountBoxIcon /> },
                { value: 3, label: t('quickSaleSettingsModal.tabs.warehouseStock'), icon: <WarehouseIcon /> },
                { value: 4, label: t('quickSaleSettingsModal.tabs.printing'), icon: <PrintIcon /> },
                { value: 5, label: t('quickSaleSettingsModal.tabs.extraSettings'), icon: <SettingsIcon /> },
                { value: 6, label: t('quickSaleSettingsModal.tabs.localService'), icon: <DnsIcon /> },
                { value: 7, label: t('quickSaleSettingsModal.tabs.customerDisplay'), icon: <TvIcon /> },
                { value: 8, label: t('quickSaleSettingsModal.tabs.clubEntry'), icon: <MeetingRoomIcon /> },
                ...(isPackageMode ? [{ value: 9, label: t('quickSaleSettingsModal.tabs.integrations'), icon: <IntegrationInstructionsIcon /> }] : []),
                { value: 10, label: t('quickSaleSettingsModal.tabs.pavoDevices'), icon: <PointOfSaleIcon /> }, // ✅ YENİ
                { value: 11, label: t('quickSaleSettingsModal.tabs.kioskDevices'), icon: <DesktopWindowsIcon /> }, // 🖥️ Plan 2 — Kiosk Cihazlari
                { value: 12, label: t('quickSaleSettingsModal.tabs.itemNotes'), icon: <NoteAddIcon /> }
              ];
          
          const currentTab = tabs.find(t => t.value === activeTab);
          
          return (
            <>
              {/* 📱 Mobil: Tab Seçim Butonu + Drawer */}
              {isMobile && (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      console.log('📱 Tab menü butonu tıklandı');
                      setMobileTabDrawerOpen(true);
                    }}
                    sx={{ 
                      p: 2, 
                      borderRadius: 0,
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderTop: 'none',
                      borderBottom: 2,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'grey.100',
                        borderColor: 'divider',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box 
                        sx={{ 
                          bgcolor: isRestaurantMode ? 'warning.main' : 'primary.main',
                          color: 'white',
                          borderRadius: 1,
                          p: 0.75,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {currentTab?.icon}
                      </Box>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('quickSaleSettingsModal.selectedSection')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="text.primary">
                          {currentTab?.label || t('quickSaleSettingsModal.selectMenu')}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={t('quickSaleSettingsModal.change')} 
                      size="small" 
                      color={isRestaurantMode ? 'warning' : 'primary'}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Button>
                  
                  {/* Mobil Tab Drawer */}
                  <Drawer
                    anchor="bottom"
                    open={mobileTabDrawerOpen}
                    onClose={() => setMobileTabDrawerOpen(false)}
                    ModalProps={{
                      sx: { zIndex: 1400 } // Dialog'un üstünde
                    }}
                    PaperProps={{
                      sx: {
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        maxHeight: '70vh'
                      }
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="h6" fontWeight="bold">
                        {isRestaurantMode ? t('quickSaleSettingsModal.titles.restaurantSettings') : t('quickSaleSettingsModal.titles.settings')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('quickSaleSettingsModal.selectSectionHint')}
                      </Typography>
                    </Box>
                    <List sx={{ pt: 0 }}>
                      {tabs.map((tab) => (
                        <ListItemButton
                          key={tab.value}
                          selected={activeTab === tab.value}
                          onClick={() => {
                            setActiveTab(tab.value);
                            setMobileTabDrawerOpen(false);
                          }}
                          sx={{
                            py: 1.5,
                            '&.Mui-selected': {
                              bgcolor: isRestaurantMode ? 'warning.light' : 'primary.light',
                              '&:hover': {
                                bgcolor: isRestaurantMode ? 'warning.light' : 'primary.light',
                              }
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40, color: activeTab === tab.value ? (isRestaurantMode ? 'warning.main' : 'primary.main') : 'text.secondary' }}>
                            {tab.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={tab.label}
                            primaryTypographyProps={{
                              fontWeight: activeTab === tab.value ? 'bold' : 'normal'
                            }}
                          />
                          {activeTab === tab.value && (
                            <Chip 
                              label={t('quickSaleSettingsModal.selected')} 
                              size="small" 
                              color={isRestaurantMode ? 'warning' : 'primary'}
                              sx={{ height: 24 }}
                            />
                          )}
                        </ListItemButton>
                      ))}
                    </List>
                  </Drawer>
                </>
              )}
              
              {/* 🖥️ Desktop: Normal Tabs */}
              {!isMobile && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                  {/* Sekme sayısı bir satıra sığmadığı için kaydırma yerine satır sarması
                      kullanılıyor: sığmayan sekmeler alt satıra iner. MUI'nin alt çizgi
                      göstergesi sarılan satırlarda yanlış konumlandığı için kapatıldı;
                      seçili sekme arka plan + kalın yazı ile belirtiliyor. */}
                  <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    aria-label={t('quickSaleSettingsModal.tabsAriaLabel')}
                    variant="standard"
                    textColor="primary"
                    TabIndicatorProps={{ sx: { display: 'none' } }}
                    sx={{
                      minHeight: 0,
                      px: 0.5,
                      '& .MuiTabs-flexContainer': {
                        flexWrap: 'wrap',
                        rowGap: 0.25,
                      },
                    }}
                  >
                    {tabs.map((tab) => (
                      <Tab 
                        key={tab.value}
                        value={tab.value} 
                        label={tab.label} 
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          minHeight: 38,
                          minWidth: 'auto',
                          py: 0.5,
                          px: 1.25,
                          borderRadius: 1,
                          '&.Mui-selected': {
                            fontWeight: 700,
                            bgcolor: 'action.selected',
                          },
                        }} 
                      />
                    ))}
                  </Tabs>
                </Box>
              )}
            </>
          );
        })()}

        <Box sx={{flexGrow: 1, overflowY: 'auto', p: 0}}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2 }}>
              <CircularProgress size={30}/>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>
          ) : (
            <>
              {(() => {
                console.log('🟢 [RENDER CHECK] activeTab:', activeTab, 'isRestaurantMode:', isRestaurantMode);
                return null;
              })()}
              
              {/* ✅ RESTORAN MOD: Tab 0 = Masa Grupları */}
              {isRestaurantMode && activeTab === 0 && restaurantScreenId && (
                <Paper elevation={0}>
                  <RestaurantTableGroupsTab
                    restaurantScreenId={restaurantScreenId}
                    companyId={companyId || ''}
                  />
                </Paper>
              )}
              
              {/* ✅ RESTORAN MOD: Tab 1 = Masalar */}
              {isRestaurantMode && activeTab === 1 && restaurantScreenId && (
                <Paper elevation={0}>
                  <RestaurantTablesTab
                    restaurantScreenId={restaurantScreenId}
                    companyId={companyId || ''}
                    onOpenManageGroupsModal={() => setActiveTab(0)}
                  />
                </Paper>
              )}
              
              {/* Ödeme Yöntemleri: Normal mod Tab 0, Restoran mod Tab 2 */}
              {((isRestaurantMode && activeTab === 2) || (!isRestaurantMode && activeTab === 0)) && (
                <Paper elevation={0}>
                  <PaymentSettingsTab
                    paymentOptions={paymentOptions}
                    allCashboxes={allCashboxes}
                    allAccounts={allAccounts}
                    allBankAccounts={allBankAccounts}
                    loading={loading}
                    prepaidSystemEnabled={prepaidSystemEnabled}
                    onPaymentOptionFieldChange={handlePaymentOptionFieldChange}
                    onAddPaymentOption={handleAddPaymentOption}
                    onDeletePaymentOption={handleDeletePaymentOption}
                    onPrepaidSystemChange={(enabled: boolean) => setPrepaidSystemEnabled(enabled)}
                    useFiscalDeviceForCash={useFiscalDeviceForCash}
                    onUseFiscalDeviceForCashChange={(enabled: boolean) => setUseFiscalDeviceForCash(enabled)}
                    selectedFiscalDevice={selectedFiscalDevice}
                    onFiscalDeviceChange={(device: string) => setSelectedFiscalDevice(device)}
                    warnIfFiscalNotConnected={warnIfFiscalNotConnected}
                    onWarnIfFiscalNotConnectedChange={(enabled: boolean) => setWarnIfFiscalNotConnected(enabled)}
                    showCashChangeModal={showCashChangeModal}
                    onShowCashChangeModalChange={(enabled: boolean) => setShowCashChangeModal(enabled)}
                    usePavoPayForOtherPayments={usePavoPayForOtherPayments}
                    onUsePavoPayForOtherPaymentsChange={(enabled: boolean) => setUsePavoPayForOtherPayments(enabled)}
                    useExternalPrinterForPavo={useExternalPrinterForPavo}
                    onUseExternalPrinterForPavoChange={(enabled: boolean) => setUseExternalPrinterForPavo(enabled)}
                    pavoPaymentMediatorId={pavoPaymentMediatorId}
                    onPavoPaymentMediatorChange={(mediatorId: number) => setPavoPaymentMediatorId(mediatorId)}
                    useRestP2PForPayments={useRestP2PForPayments}
                    onUseRestP2PForPaymentsChange={(enabled: boolean) => setUseRestP2PForPayments(enabled)}
                    pavoRestP2PDeviceScreenCode={pavoRestP2PDeviceScreenCode}
                    pavoRestP2PDeviceName={pavoRestP2PDeviceName}
                    onPavoRestP2PDeviceChange={(device) => {
                      if (device) {
                        setPavoRestP2PDeviceScreenCode(device.code);
                        setPavoRestP2PDeviceName(device.name);
                      } else {
                        setPavoRestP2PDeviceScreenCode(null);
                        setPavoRestP2PDeviceName(null);
                      }
                    }}
                    companyId={companyId || undefined}
                    branchId={branchId || undefined}
                    onCashboxCreated={handleQuickCreatedCashbox}
                    onBankAccountCreated={handleQuickCreatedBankAccount}
                  />
                </Paper>
              )}
              
              {/* Login İşlemleri (personel atama + cihaz kodları): Normal mod Tab 1, Restoran mod Tab 3 */}
              {((isRestaurantMode && activeTab === 3) || (!isRestaurantMode && activeTab === 1)) && (
                <PersonnelSettingsTab
                  companyId={companyId}
                  terminalDeviceMode={terminalDeviceMode}
                  terminalLinkedScreenId={terminalLinkedScreenId}
                  screenName={screenName}
                  allPersonnel={allPersonnel}
                  selectedPersonnelIds={selectedPersonnelIds}
                  selectedPersonnelForPermissions={selectedPersonnelForPermissions}
                  personnelPermissionsMap={personnelPermissionsMap}
                  loading={loading}
                  onPersonnelSelectionChange={(newSelectedIds: string[]) => {
                            setSelectedPersonnelIds(newSelectedIds);
                            if (selectedPersonnelForPermissions && !newSelectedIds.includes(selectedPersonnelForPermissions)) {
                              setSelectedPersonnelForPermissions(null);
                            }
                          }}
                  onSelectPersonnelForPermissions={handleSelectPersonnelForPermissions}
                  onPermissionChange={handlePermissionChange}
                            />
              )}
              
              {/* ✅ RESTORAN MOD: Tab 4 = Garson Atama */}
              {isRestaurantMode && activeTab === 4 && restaurantScreenId && (
                <Paper elevation={0}>
                  <RestaurantWaitersTab
                    restaurantScreenId={restaurantScreenId}
                  />
                </Paper>
              )}
              
              {/* ✅ YENİ: Kasiyer Atama: Normal mod Tab 2, Restoran mod Tab 5 */}
              {((isRestaurantMode && activeTab === 5) || (!isRestaurantMode && activeTab === 2)) && screenId && companyId && (
                <Paper elevation={0}>
                  {/* 🔴 DEBUG LOG: Kasiyer Atama Tabı Açıldı */}
                  {(() => {
                    console.log('═══════════════════════════════════════════════════════════');
                    console.log('🔴 [CASHIER TAB] Kasiyer Atama Tabı Render');
                    console.log('🔴 [CASHIER TAB] useCashierSystem STATE değeri:', useCashierSystem);
                    console.log('🔴 [CASHIER TAB] initialUseCashierSystem değeri:', initialUseCashierSystem);
                    console.log('🔴 [CASHIER TAB] screenId:', screenId);
                    console.log('═══════════════════════════════════════════════════════════');
                    return null;
                  })()}
                  {/* ✅ Kasiyer Sistemi Aktifleştirme Switch'i (sadece Normal Hızlı Satış için) */}
                  {!isRestaurantMode && (
                    <Box sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useCashierSystem}
                            onChange={(e) => {
                              console.log('🟡 [SWITCH CHANGE] Eski değer:', useCashierSystem, '-> Yeni değer:', e.target.checked);
                              setUseCashierSystem(e.target.checked);
                            }}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {t('quickSaleSettingsModal.cashierSystem.title')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('quickSaleSettingsModal.cashierSystem.description')}
                            </Typography>
                          </Box>
                        }
                        sx={{ ml: 0, width: '100%' }}
                      />
                    </Box>
                  )}
                  {/* ✅ YENİ: Cari Tahsilat İzni Switch'i (ekran seviyesi ana kapı) */}
                  <Box sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={allowAccountCollection}
                          onChange={(e) => setAllowAccountCollection(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            Cari Tahsilat Yapılabilsin
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Bu ekranda (ve bağlı Pavo cihazlarında) cari hesaplardan tahsilat alınabilir. Kasiyer bazlı yetki için aşağıdaki kasiyer listesinden ayrıca izin verilmelidir.
                          </Typography>
                        </Box>
                      }
                      sx={{ ml: 0, width: '100%' }}
                    />
                  </Box>
                  <CashierSettingsTab
                    screenId={isRestaurantMode && restaurantScreenId ? restaurantScreenId : parseInt(screenId, 10)}
                    screenType={isRestaurantMode ? 'Restaurant' : 'QuickSale'}
                    companyId={companyId}
                  />
                </Paper>
              )}
              
              {/* Depo/Stok: Normal mod Tab 3, Restoran mod Tab 6 */}
              {((isRestaurantMode && activeTab === 6) || (!isRestaurantMode && activeTab === 3)) && (
                <WarehouseStockSettingsTab
                  allWarehouses={allWarehouses}
                  allCategories={allCategories}
                  selectedWarehouseIds={selectedWarehouseIds}
                  selectedCategoriesPerWarehouse={selectedCategoriesPerWarehouse}
                  selectedProductsPerCategory={selectedProductsPerCategory}
                  selectedProductsDetails={selectedProductsDetails}
                  activeWarehouseSubTab={activeWarehouseSubTab}
                  activeCategorySubTabPerWarehouse={activeCategorySubTabPerWarehouse}
                  selectedProductsPage={selectedProductsPage}
                  selectedProductsRowsPerPage={selectedProductsRowsPerPage}
                  preventNegativeStock={preventNegativeStock}
                  showProductImages={showProductImages}
                  onWarehouseSelectionModalOpen={() => setIsWarehouseSelectionModalOpen(true)}
                  onActiveWarehouseSubTabChange={handleActiveWarehouseSubTabChange}
                  onRemoveWarehouse={handleRemoveWarehouse}
                  onRemoveCategory={handleRemoveCategory}
                  onRemoveProduct={handleRemoveProduct} // ✅ YENİ
                  onMoveCategory={handleMoveCategory} // ✅ YENİ: Kategori sıralama
                  onActiveCategorySubTabChange={handleActiveCategorySubTabChange}
                  onOpenInventoryCategoryModal={handleOpenInventoryCategoryModal}
                  onOpenProductModal={handleOpenProductModal}
                  onSelectedProductsPageChange={handleSelectedProductsPageChange}
                  onSelectedProductsRowsPerPageChange={handleSelectedProductsRowsPerPageChange}
                  onPreventNegativeStockChange={(checked: boolean) => setPreventNegativeStock(checked)}
                  onShowProductImagesChange={(checked: boolean) => setShowProductImages(checked)}
                  onUploadProductImage={(productId: string) => handleUploadProductImage(productId)}
                  allowAccountCreationOnSale={allowAccountCreationOnSale} // YENİ PROP
                  onAllowAccountCreationOnSaleChange={(checked: boolean) => setAllowAccountCreationOnSale(checked)} // YENİ PROP
                  // ✅ YENİ: Şube paket fiyatı kullan
                  usePackagePrices={usePackagePrices}
                  onUsePackagePricesChange={(checked: boolean) => setUsePackagePrices(checked)}
                  hasBranchAssigned={!!branchId}
                  // ✅ YENİ: Depo/Stok taslak sistemi (şirket genel, isimli taslaklar)
                  companyId={companyId}
                  onApplyWarehouseTemplate={applyWarehouseTemplateToWarehouse}
                  onChangeWarehouse={handleChangeWarehouse}
                />
              )}
              
              {/* Yazdırma: Normal mod Tab 4, Restoran mod Tab 7 */}
              {((isRestaurantMode && activeTab === 7) || (!isRestaurantMode && activeTab === 4)) && (
                <PrintingSettingsTab
                  printingMode={printingMode}
                  defaultReceiptPrinter={defaultReceiptPrinter}
                  definedPrinters={definedPrinters}
                  categoryPrinterAssignments={categoryPrinterAssignments}
                  categoryPrinterAssignments2={categoryPrinterAssignments2}
                  uniqueCategoryIds={uniqueCategoryIds}
                  categoriesToListForPrinting={categoriesToListForPrinting}
                  localPrinters={localPrinters}
                  loadingLocalPrinters={loadingLocalPrinters}
                  localPrintersError={localPrintersError}
                  onPrintingModeChange={handlePrintingModeChange}
                  onSetDefaultPrinter={handleSetDefaultPrinter}
                  onRemoveDefaultPrinter={handleRemoveDefaultPrinter}
                  onCategoryPrinterChange={handleCategoryPrinterChange}
                  onCategoryPrinterChange2={handleCategoryPrinterChange2}
                  onRefreshLocalPrinters={() => {
                    // Yazdırma tab'ı: Normal mod Tab 3, Restoran mod Tab 6
                    if (open && ((isRestaurantMode && activeTab === 7) || (!isRestaurantMode && activeTab === 4))) {
                      const fetchLocalPrinters = async () => {
                        setLoadingLocalPrinters(true);
                        setLocalPrintersError(null);
                        setLocalPrinters([]);
                        try {
                          const response = await fetch('http://localhost:3003/api/yazicilariGetir');
                          if (!response.ok) {
                            let errorMessage = `Yerel yazıcı servisine ulaşılamadı (Kod: ${response.status})`;
                            try {
                              const errorData = await response.json();
                              errorMessage = errorData.message || errorData.error || errorMessage;
                            } catch (e) {
                              errorMessage = `${errorMessage}: ${response.statusText}`;
                            }
                            throw new Error(errorMessage);
                          }
                          const printersFromService: Array<{ id: string; name: string }> = await response.json();
                          setLocalPrinters(printersFromService.map(p => ({ id: p.id, name: p.name })));
                        } catch (err: any) {
                          console.error("Yerel yazıcılar alınırken hata:", err);
                          setLocalPrintersError(err.message || 'Yerel yazıcılar alınamadı. Lütfen yerel servisin çalıştığından emin olun.');
                        } finally {
                          setLoadingLocalPrinters(false);
                        }
                      };
                      fetchLocalPrinters();
                    }
                  }}
                  onPrintTestPage={handlePrintTestPage}
                  printTestLoading={printTestLoading}
                  onOpenReceiptSettingsModal={handleOpenReceiptSettingsModal}
                  onResetReceiptNumber={handleResetReceiptNumber}
                  receiptNumberEnabled={receiptSettings.showReceiptNumber}
                  // ✅ YENİ: Adisyon yazıcısı (kategori bazlı modda restoran için)
                  adisyonPrinter={adisyonPrinter}
                  onAdisyonPrinterChange={(printer) => setAdisyonPrinter(printer)}
                  // ✅ YENİ: Kategorisiz ürün yazıcısı (kategori bazlı mod)
                  uncategorizedPrinter={uncategorizedPrinter}
                  onUncategorizedPrinterChange={(printer) => setUncategorizedPrinter(printer)}
                  // ✅ YENİ: Yazarkasa + Fiş Yazıcısı birlikte kullanım
                  useBothFiscalAndReceiptPrinter={useBothFiscalAndReceiptPrinter}
                  onUseBothFiscalAndReceiptPrinterChange={(enabled) => setUseBothFiscalAndReceiptPrinter(enabled)}
                  // ✅ YENİ: Restoran modu kontrolü
                  isRestaurantMode={isRestaurantMode}
                />
              )}
              
              {/* Ek Ayarlar: Normal mod Tab 5, Restoran mod Tab 8 */}
              {((isRestaurantMode && activeTab === 8) || (!isRestaurantMode && activeTab === 5)) && (
                <Paper elevation={0} sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{t('quickSaleSettingsModal.extraSettings.title')}</Typography>
                  </Box>
                  
                  <Alert severity="info" sx={{ mb: 3 }}>
                    {t('quickSaleSettingsModal.extraSettings.description')}
                  </Alert>
                  
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Mutfak Ekranı Ayarları */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <RestaurantIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {t('quickSaleSettingsModal.extraSettings.kitchenIntegration.title')}
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={useKitchenScreen}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setUseKitchenScreen(isChecked);
                                  if (isChecked) {
                                    setIsQRMenuSelectionModalOpen(true);
                                  } else {
                                    setSelectedQRMenuId(null);
                                    setSelectedQRMenuName('');
                                  }
                                }}
                                color="primary"
                              />
                            }
                            label={useKitchenScreen ? t('quickSaleSettingsModal.extraSettings.common.active') : t('quickSaleSettingsModal.extraSettings.common.inactive')}
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('quickSaleSettingsModal.extraSettings.kitchenIntegration.subtitle')}
                        </Typography>
                        
                        {useKitchenScreen && selectedQRMenuId && (
                          <Box sx={{ mt: 1, p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="success.contrastText" fontWeight="bold">
                                ✅ {selectedQRMenuName}
                              </Typography>
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<RestaurantMenuIcon />}
                                onClick={() => setIsQRMenuSelectionModalOpen(true)}
                                sx={{ color: 'success.contrastText', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                              >
                                {t('quickSaleSettingsModal.change')}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Satış Ekranı Görünüm Ayarları Butonu */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {t('quickSaleSettingsModal.extraSettings.layoutButton.title')}
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={showScreenLayoutButton}
                                onChange={(e) => setShowScreenLayoutButton(e.target.checked)}
                                color="primary"
                              />
                            }
                            label={showScreenLayoutButton ? t('quickSaleSettingsModal.extraSettings.layoutButton.visible') : t('quickSaleSettingsModal.extraSettings.layoutButton.hidden')}
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                          {t('quickSaleSettingsModal.extraSettings.layoutButton.subtitle')}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Oto Kilitleme Ayarları */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LockClockIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {t('quickSaleSettingsModal.extraSettings.autoLock.title')}
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={autoLockEnabled}
                                onChange={(e) => setAutoLockEnabled(e.target.checked)}
                                color="primary"
                              />
                            }
                            label={autoLockEnabled ? t('quickSaleSettingsModal.extraSettings.common.active') : t('quickSaleSettingsModal.extraSettings.common.inactive')}
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {t('quickSaleSettingsModal.extraSettings.autoLock.subtitle')}
                        </Typography>

                        {autoLockEnabled && (
                          <Box sx={{ mt: 2 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                              {t('quickSaleSettingsModal.extraSettings.autoLock.info')}
                            </Alert>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                              {t('quickSaleSettingsModal.extraSettings.autoLock.duration')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {[10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map((sec) => (
                                <Button
                                  key={sec}
                                  variant={autoLockTimeoutSeconds === sec ? 'contained' : 'outlined'}
                                  color={autoLockTimeoutSeconds === sec ? 'primary' : 'inherit'}
                                  size="small"
                                  onClick={() => setAutoLockTimeoutSeconds(sec)}
                                  sx={{ minWidth: 56 }}
                                >
                                  {t('quickSaleSettingsModal.extraSettings.autoLock.seconds', { count: sec })}
                                </Button>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Hazır Ürün Silme Nedenleri */}
                    {isRestaurantMode && (
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <DeleteIcon sx={{ mr: 1, color: 'error.main' }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {t('quickSaleSettingsModal.extraSettings.deleteReasons.title')}
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('quickSaleSettingsModal.extraSettings.deleteReasons.description')}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                              size="small"
                              label={t('quickSaleSettingsModal.extraSettings.deleteReasons.newReasonLabel')}
                              value={newDeleteReason}
                              onChange={(e) => setNewDeleteReason(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newDeleteReason.trim()) {
                                  if (!predefinedDeleteReasons.includes(newDeleteReason.trim())) {
                                    setPredefinedDeleteReasons([...predefinedDeleteReasons, newDeleteReason.trim()]);
                                  }
                                  setNewDeleteReason('');
                                }
                              }}
                              sx={{ flex: 1 }}
                              placeholder={t('quickSaleSettingsModal.extraSettings.deleteReasons.newReasonPlaceholder')}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<AddIcon />}
                              disabled={!newDeleteReason.trim() || predefinedDeleteReasons.includes(newDeleteReason.trim())}
                              onClick={() => {
                                if (newDeleteReason.trim() && !predefinedDeleteReasons.includes(newDeleteReason.trim())) {
                                  setPredefinedDeleteReasons([...predefinedDeleteReasons, newDeleteReason.trim()]);
                                  setNewDeleteReason('');
                                }
                              }}
                            >
                              {t('quickSaleSettingsModal.extraSettings.deleteReasons.add')}
                            </Button>
                          </Box>

                          {predefinedDeleteReasons.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {predefinedDeleteReasons.map((reason, index) => (
                                <Chip
                                  key={index}
                                  label={reason}
                                  onDelete={() => {
                                    setPredefinedDeleteReasons(predefinedDeleteReasons.filter((_, i) => i !== index));
                                  }}
                                  color="error"
                                  variant="outlined"
                                  sx={{ fontSize: '0.9rem' }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                              {t('quickSaleSettingsModal.extraSettings.deleteReasons.empty')}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              )}
              
              {/* Yerel Servis: Normal mod Tab 6, Restoran mod Tab 9 */}
              {((isRestaurantMode && activeTab === 9) || (!isRestaurantMode && activeTab === 6)) && (
                <Paper elevation={0} sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <DnsIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{t('quickSaleSettingsModal.localServiceSettings.title')}</Typography>
                  </Box>
                  
                  <Alert severity="info" sx={{ mb: 3 }}>
                    {t('quickSaleSettingsModal.localServiceSettings.description')}
                  </Alert>
                  
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                      {t('quickSaleSettingsModal.localServiceSettings.urlTitle')}
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label={t('quickSaleSettingsModal.localServiceSettings.urlLabel')}
                      value={localServiceUrl}
                      onChange={(e) => setLocalServiceUrl(e.target.value)}
                      placeholder="http://localhost:3003"
                      helperText={t('quickSaleSettingsModal.localServiceSettings.urlHelper')}
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setLocalServiceUrl('http://localhost:3003')}
                        disabled={localServiceUrl === 'http://localhost:3003'}
                      >
                        {t('quickSaleSettingsModal.localServiceSettings.resetDefault')}
                      </Button>
                      
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        onClick={async () => {
                          try {
                            const response = await fetch(`${localServiceUrl}/api/yazicilariGetir`, {
                              method: 'GET',
                              signal: AbortSignal.timeout(5000)
                            });
                            if (response.ok) {
                              setSnackbarMessage(t('quickSaleSettingsModal.localServiceSettings.connectionSuccess'));
                              setSnackbarSeverity('success');
                            } else {
                              setSnackbarMessage(t('quickSaleSettingsModal.localServiceSettings.connectionErrorStatus', { status: response.status }));
                              setSnackbarSeverity('error');
                            }
                          } catch (err: any) {
                            setSnackbarMessage(t('quickSaleSettingsModal.localServiceSettings.connectionFailed', { message: err.message || t('quickSaleSettingsModal.localServiceSettings.unknownError') }));
                            setSnackbarSeverity('error');
                          }
                          setSnackbarOpen(true);
                        }}
                      >
                        {t('quickSaleSettingsModal.localServiceSettings.testConnection')}
                      </Button>

                    </Box>
                  </Box>

                  {/* ── Tartı ───────────────────────────────────────────
                      Cihaz tanımı (IP, port, model) Cihaz Yönetimi > Tartılar
                      ekranında. Burada yalnızca bu kasanın hangisini
                      kullanacağı seçiliyor. */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ScaleIcon fontSize="small" /> Tartı
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={useScale}
                          onChange={(e) => {
                            setUseScale(e.target.checked);
                            if (!e.target.checked) setSelectedScaleId(null);
                          }}
                        />
                      }
                      label="Tartı kullan"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Kapalıyken kg penceresi eskisi gibi yalnızca elle giriş kabul eder,
                      tartıya hiç bağlanılmaz.
                    </Typography>

                    {useScale && (
                      <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                        {availableScales.length === 0 ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Tanımlı tartı yok. Cihaz Yönetimi &gt; Tartılar ekranından
                            tartıyı ekleyip buraya dönün.
                          </Alert>
                        ) : (
                          <TextField
                            select
                            size="small"
                            label="Kullanılacak tartı"
                            value={selectedScaleId ?? ''}
                            onChange={(e) => setSelectedScaleId(e.target.value ? Number(e.target.value) : null)}
                            sx={{ minWidth: 280, mb: 2 }}
                          >
                            <MenuItem value="">Seçilmedi</MenuItem>
                            {availableScales.map((s) => (
                              <MenuItem key={s.id} value={s.id}>
                                {s.name} — {s.host}:{s.port}
                                {s.branchName ? ` (${s.branchName})` : ''}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}

                        <TextField
                          select
                          size="small"
                          label="Okuma şekli"
                          value={scaleReadMode}
                          onChange={(e) => setScaleReadMode(e.target.value)}
                          sx={{ minWidth: 280, display: 'block' }}
                          helperText={
                            scaleReadMode === 'live'
                              ? 'Pencere açıkken ağırlık izlenir, oturunca kendiliğinden dolar.'
                              : 'Pencere açılınca bir kez okunur; kasiyer isterse yeniler.'
                          }
                        >
                          <MenuItem value="live">Canlı takip</MenuItem>
                          <MenuItem value="single">Tek sorgu</MenuItem>
                        </TextField>
                      </Box>
                    )}

                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Switch
                          checked={readWeightBarcode}
                          onChange={(e) => setReadWeightBarcode(e.target.checked)}
                        />
                      }
                      label="Ağırlık barkodu oku"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Reyondaki tartının bastığı etiketleri okur. Kasaya tartı bağlamayı
                      gerektirmez, bu yüzden üstteki ayardan bağımsızdır.
                    </Typography>
                  </Box>
                  
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>{t('quickSaleSettingsModal.localServiceSettings.noteTitle')}</strong> {t('quickSaleSettingsModal.localServiceSettings.note')}
                    </Typography>
                  </Alert>
                </Paper>
              )}
              
              {/* ✅ YENİ: Müşteri Ekranı: Normal mod Tab 7, Restoran mod Tab 10 */}
              {((isRestaurantMode && activeTab === 10) || (!isRestaurantMode && activeTab === 7)) && screenId && companyId && (
                <Paper elevation={0}>
                  <CustomerDisplaySettingsTab
                    screenId={parseInt(screenId)}
                    companyId={companyId}
                    settings={customerDisplaySettings}
                    initialSettings={initialCustomerDisplaySettings}
                    onSettingsChange={setCustomerDisplaySettings}
                    saving={saving}
                  />
                </Paper>
              )}
              
              {/* Kulüp Girişi: Normal mod Tab 8 */}
              {!isRestaurantMode && activeTab === 8 && screenId && companyId && (
                <ClubEntrySettingsTab
                  screenId={parseInt(screenId)}
                  companyId={companyId}
                  localServiceUrl={localServiceUrl}
                  onSettingsChanged={() => setClubEntryChanged(true)}
                />
              )}
              
              {/* ✅ YENİ: Entegrasyonlar: Normal mod Tab 9 (sadece paket modunda) */}
              {!isRestaurantMode && activeTab === 9 && isPackageMode && (
                <Paper elevation={0} sx={{ p: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IntegrationInstructionsIcon color="primary" />
                      Sipariş Kaynağı Görünürlüğü
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Paket sipariş ekranında hangi sipariş kaynağı tablarının görüneceğini ayarlayın.
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Telefon */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PhoneIcon sx={{ color: '#4CAF50' }} />
                      <Typography variant="body1">Telefon Siparişleri</Typography>
                    </Box>
                    <Switch
                      checked={integrationSettings.showPhoneOrders}
                      onChange={(e) => setIntegrationSettings(prev => ({ ...prev, showPhoneOrders: e.target.checked }))}
                      color="primary"
                    />
                  </Box>
                  
                  {/* Yemek Sepeti */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <DeliveryDiningIcon sx={{ color: '#FA0050' }} />
                      <Typography variant="body1">Yemek Sepeti</Typography>
                    </Box>
                    <Switch
                      checked={integrationSettings.showYemekSepetiOrders}
                      onChange={(e) => setIntegrationSettings(prev => ({ ...prev, showYemekSepetiOrders: e.target.checked }))}
                      color="primary"
                    />
                  </Box>
                  
                  {/* Trendyol */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <DeliveryDiningIcon sx={{ color: '#F27A1A' }} />
                      <Typography variant="body1">Trendyol</Typography>
                    </Box>
                    <Switch
                      checked={integrationSettings.showTrendyolOrders}
                      onChange={(e) => setIntegrationSettings(prev => ({ ...prev, showTrendyolOrders: e.target.checked }))}
                      color="primary"
                    />
                  </Box>
                  
                  {/* Getir */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <ShoppingCartIcon sx={{ color: '#5D3EBC' }} />
                      <Typography variant="body1">Getir</Typography>
                    </Box>
                    <Switch
                      checked={integrationSettings.showGetirOrders}
                      onChange={(e) => setIntegrationSettings(prev => ({ ...prev, showGetirOrders: e.target.checked }))}
                      color="primary"
                    />
                  </Box>
                  
                  {/* Migros */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <StorefrontIcon sx={{ color: '#F58220' }} />
                      <Typography variant="body1">Migros</Typography>
                    </Box>
                    <Switch
                      checked={integrationSettings.showMigrosOrders}
                      onChange={(e) => setIntegrationSettings(prev => ({ ...prev, showMigrosOrders: e.target.checked }))}
                      color="primary"
                    />
                  </Box>
                  
                  {/* Kiosk */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PointOfSaleIcon sx={{ color: '#607D8B' }} />
                      <Typography variant="body1">Kiosk</Typography>
                    </Box>
                    <Switch
                      checked={integrationSettings.showKioskOrders}
                      onChange={(e) => setIntegrationSettings(prev => ({ ...prev, showKioskOrders: e.target.checked }))}
                      color="primary"
                    />
                  </Box>
                  
                  {/* QR Menü */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <QrCode2Icon sx={{ color: '#2196F3' }} />
                      <Typography variant="body1">QR Menü</Typography>
                    </Box>
                    <Switch
                      checked={integrationSettings.showQrMenuOrders}
                      onChange={(e) => setIntegrationSettings(prev => ({ ...prev, showQrMenuOrders: e.target.checked }))}
                      color="primary"
                    />
                  </Box>
                  
                  {/* E-Ticaret */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <ShoppingBagIcon sx={{ color: '#9C27B0' }} />
                      <Typography variant="body1">E-Ticaret</Typography>
                    </Box>
                    <Switch
                      checked={integrationSettings.showEcommerceOrders}
                      onChange={(e) => setIntegrationSettings(prev => ({ ...prev, showEcommerceOrders: e.target.checked }))}
                      color="primary"
                    />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Oto Kabul ve Yazdırma */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockClockIcon color="primary" />
                      Oto Kabul ve Yazdırma
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Yeni sipariş geldiğinde belirlenen süre sonunda otomatik kabul ve fiş yazdırma.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body1" fontWeight="medium">Oto Kabul</Typography>
                    <Switch
                      checked={integrationSettings.autoAcceptEnabled}
                      onChange={(e) => setIntegrationSettings(prev => ({ ...prev, autoAcceptEnabled: e.target.checked }))}
                      color="primary"
                    />
                  </Box>

                  {integrationSettings.autoAcceptEnabled && (
                    <>
                      <Box sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Oto Kabul Süresi (saniye)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {[5, 10, 15, 20, 30, 45, 60].map((sec) => (
                            <Button
                              key={sec}
                              variant={integrationSettings.autoAcceptDelaySeconds === sec ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => setIntegrationSettings(prev => ({ ...prev, autoAcceptDelaySeconds: sec }))}
                              sx={{ minWidth: 50 }}
                            >
                              {sec} sn
                            </Button>
                          ))}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                        {t('quickSaleSettingsModal.integration.autoPrintSettings')}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body1">{t('quickSaleSettingsModal.integration.kitchenReceipt')}</Typography>
                        <Switch
                          checked={integrationSettings.autoPrintKitchen}
                          onChange={(e) => setIntegrationSettings(prev => ({ ...prev, autoPrintKitchen: e.target.checked }))}
                          color="primary"
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body1">{t('quickSaleSettingsModal.integration.orderReceipt')}</Typography>
                        <Switch
                          checked={integrationSettings.autoPrintReceiptOnAccept}
                          onChange={(e) => setIntegrationSettings(prev => ({ ...prev, autoPrintReceiptOnAccept: e.target.checked }))}
                          color="primary"
                        />
                      </Box>
                    </>
                  )}
                </Paper>
              )}

              {/* ✅ YENİ: Pavo Cihazları: Normal mod Tab 10, Restoran mod Tab 11 */}
              {(
                (!isRestaurantMode && activeTab === 10) ||
                (isRestaurantMode && activeTab === 11)
              ) && (
                <Box sx={{ p: 2 }}>
                  <PavoDevicesTab
                    screenId={screenId ? parseInt(screenId, 10) : null}
                    companyId={companyId}
                    branchId={branchId ?? undefined}
                    screenName={screenName}
                    screenType={isRestaurantMode ? 'restaurant' : 'quicksale'}
                  />
                </Box>
              )}

              {/* 🖥️ Plan 2 — Kiosk Cihazları: Normal mod Tab 11, Restoran mod Tab 12 */}
              {(
                (!isRestaurantMode && activeTab === 11) ||
                (isRestaurantMode && activeTab === 12)
              ) && screenId && companyId && branchId && (
                <Box sx={{ p: 2 }}>
                  <KioskAssignmentTab
                    screenId={parseInt(screenId, 10)}
                    companyId={companyId}
                    branchId={branchId}
                  />
                </Box>
              )}

              {/* 📝 Ürün Açıklamaları: Normal mod Tab 12, Restoran mod Tab 13 */}
              {(
                (!isRestaurantMode && activeTab === 12) ||
                (isRestaurantMode && activeTab === 13)
              ) && (
                <ItemNotesSettingsTab
                  notes={predefinedItemNotes}
                  onChange={setPredefinedItemNotes}
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', justifyContent: 'flex-end' }}>
        <Box>
          <Button onClick={onClose} color="inherit" disabled={saving} size="small" sx={{mr:1}}>
            {t('quickSaleSettingsModal.footer.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || loading || !hasChangesComparedToSQL}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            size="small"
          >
            {saving ? t('quickSaleSettingsModal.footer.saving') : t('quickSaleSettingsModal.footer.saveChanges')}
          </Button>
        </Box>
      </DialogActions>

      <WarehouseSelectionModal 
            open={isWarehouseSelectionModalOpen} 
            onClose={() => setIsWarehouseSelectionModalOpen(false)}
            allWarehouses={allWarehouses}
            selectedIds={selectedWarehouseIds}
            onSave={handleWarehouseSelectionSave}
        />
        <InventoryCategoryModal 
            open={isInventoryCategoryModalOpen}
            onClose={handleCloseInventoryCategoryModal}
            salesScreenMode={!!currentWarehouseIdForCategoryModal}
            salesSelectedCategoryIds={currentWarehouseIdForCategoryModal ? (selectedCategoriesPerWarehouse[currentWarehouseIdForCategoryModal] || []) : []}
            salesTargetName={allWarehouses.find(w => w.id === currentWarehouseIdForCategoryModal)?.name}
            onAddCategoryToSalesScreen={handleAddCategoryToWarehouse}
        />
        <QuickSaleProductSelectionModal
            open={isProductSelectionModalOpen}
            onClose={() => setIsProductSelectionModalOpen(false)}
            companyId={companyId}
            allWarehouses={allWarehouses}
            allCategories={allCategories}
            initialWarehouseId={null} // ✅ DEĞİŞİKLİK: Artık otomatik seçim yok - kullanıcı kendisi seçecek
            initialCategoryId={null} // ✅ DEĞİŞİKLİK: Artık otomatik seçim yok - kullanıcı kendisi seçecek
            alreadySelectedProductIds={currentWarehouseIdForProductModal && currentCategoryIdForProductModal ? (selectedProductsPerCategory[currentWarehouseIdForProductModal]?.[currentCategoryIdForProductModal] || []) : []}
            onSelectProducts={handleProductSelectionSave}
            existingProductsDetails={selectedProductsDetails} // ✅ YENİ: Önceden seçili ürünlerin detaylarını gönder
        />
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          ContentProps={{
            sx: {
              backgroundColor: snackbarSeverity === 'success' ? 'success.main' : 'error.main',
            }
          }}
        />
        {isReceiptSettingsModalVisible && (
            <ReceiptSettingsModal
              visible={isReceiptSettingsModalVisible}
              onCancel={handleCloseReceiptSettingsModal}
              onSave={handleSaveReceiptSettings}
              initialSettings={receiptSettings}
              companyId={companyId}
            />
        )}
        {/* Resim yükleme modalı */}
        <ProductImageModal
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          onUpload={uploadImageToFirebase}
          loading={imageUploading}
        />
        
        {/* QR Menü Seçim Modalı */}
        <QRMenuSelectionModal
          open={isQRMenuSelectionModalOpen}
          onClose={() => setIsQRMenuSelectionModalOpen(false)}
          companyId={companyId}
          selectedMenuId={selectedQRMenuId}
          onSelectMenu={(menuId: string, menuName: string) => {
            setSelectedQRMenuId(menuId);
            setSelectedQRMenuName(menuName);
            setIsQRMenuSelectionModalOpen(false);
          }}
          onCancel={() => {
            // Menü seçilmeden modal kapatıldı, switch'i geri kapat
            setUseKitchenScreen(false);
            setIsQRMenuSelectionModalOpen(false);
          }}
        />

    </Dialog>
  );
};

export default QuickSaleScreenSettingsModal; 